import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Trash2, RefreshCw, Tag, Repeat, PieChart, Pencil, CheckCircle, RotateCcw, Clock, AlertCircle, Paperclip, FileText, Download, Check, Square, Package, Eye, X, ChevronDown } from 'lucide-react';
import { toast } from '../utils/toast';
import { exportarPDF, exportarWord, htmlOrcamento } from '../utils/exportDoc';
import { useEmpresa } from '../contexts/EmpresaContext';
import AnexosLancamento from './AnexosLancamento';
import { gerarCatalogoPadrao, calcularTotalOrcamento, calcularSubtotalSubmodulo, calcularTotalModulo, calcularSubtotalModuloInteiro, moduloVendidoInteiro, moduloEstaNoOrcamento, contarSubmodulosSelecionados, calcularTotaisExtras, mergeCatalogoComPrecos, extrairMapaPrecos } from '../config/modulos-kzerosystem';

// Referências estáveis pra valores vazios — usadas quando a empresa ativa
// ainda não tem entrada no respectivo mapa *PorEmpresa. Compartilhar a mesma
// referência evita invalidar useMemos a cada render.
const EMPTY_FLUXO_CAIXA = Object.freeze([]);
const EMPTY_ARR = Object.freeze([]);
const EMPTY_OBJ = Object.freeze({});

// Resolve valor namespaced por empresa com migração lazy:
// - Se o mapa tem entrada pra essa empresa, usa.
// - Se a empresa é "kzero-consultoria" (histórica) e existe valor legado, usa o legado.
// - Senão, retorna o vazio passado (mantendo referência estável).
const resolverNamespaceEmpresa = (mapaPorEmpresa, slug, legado, vazio) => {
  if (mapaPorEmpresa && mapaPorEmpresa[slug] !== undefined) return mapaPorEmpresa[slug];
  if (slug === 'kzero-consultoria' && legado !== undefined && legado !== null) return legado;
  return vazio;
};

// Calcula o 5º dia útil de um mês/ano (ignora sábado=6, domingo=0)
function quintoDiaUtil(ano, mes) {
  let diaUtil = 0;
  let data = new Date(ano, mes - 1, 1);
  while (diaUtil < 5) {
    if (data.getDay() !== 0 && data.getDay() !== 6) diaUtil++;
    if (diaUtil < 5) data.setDate(data.getDate() + 1);
  }
  return data.toISOString().split('T')[0];
}

const fmtMoeda = v => `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMes = ym => { if (!ym) return '—'; const [a, m] = ym.split('-'); return `${['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'][parseInt(m)-1]}/${a}`; };
const hoje = () => new Date().toISOString().split('T')[0];
const mesAtualStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; };

const CAT_PADRAO = ['Banco','Impostos','Fornecedores','Salários','Empréstimos','Outros'];

export const FinanceiroAvancado = ({
  fluxoCaixa: fluxoCaixaLegado = [], setFluxoCaixa: setFluxoCaixaLegado,
  fluxoCaixaPorEmpresa, setFluxoCaixaPorEmpresa,
  contasReceber: contasReceberLegado = [], setContasReceber: setContasReceberLegado,
  contasReceberPorEmpresa, setContasReceberPorEmpresa,
  categorias: catProp, setCategorias: setCatProp,
  categoriasPorEmpresa, setCategoriasPorEmpresa,
  recorrencias: recProp, setRecorrencias: setRecProp,
  recorrenciasPorEmpresa, setRecorrenciasPorEmpresa,
  quotas: quotasProp, setQuotas: setQuotasProp,
  quotasPorEmpresa, setQuotasPorEmpresa,
  acertosDiv: acertosProp, setAcertosDiv: setAcertosProp,
  acertosDivPorEmpresa, setAcertosDivPorEmpresa,
  agendamentos = [], clientes = [], reembolsos: reembolsosLegado = [], reembolsosPorEmpresa,
  contabilConfig = {},
  contasPagar: cpProp = [], setContasPagar: setCpProp,
  contasPagarPorEmpresa, setContasPagarPorEmpresa,
  mensalidadesPorEmpresa, setMensalidadesPorEmpresa,
  orcamentosKzsPorEmpresa, setOrcamentosKzsPorEmpresa,
  catalogoPrecosKzsPorEmpresa, setCatalogoPrecosKzsPorEmpresa,
  itensPadraoOrcKzsPorEmpresa, setItensPadraoOrcKzsPorEmpresa,
}) => {
  const formRef = React.useRef(null);
  const [aba, setAba] = useState('fluxo');

  // ── Empresa ativa (slug usado por todos os mapas *PorEmpresa) ──
  // Migração lazy aplicada a TODOS os campos financeiros: Kzero Consultoria
  // herda os valores legados (fluxoCaixa, fin_quotas, fin_acertos, etc.) quando
  // não há entrada própria; demais empresas começam vazias. Os valores legados
  // nunca são apagados — ficam como fonte de verdade para a Kzero Consultoria
  // até ela ter sua própria entrada gravada.
  const { empresaAtiva } = useEmpresa();
  const slugEmp = empresaAtiva?.slug || 'kzero-consultoria';

  const fluxoCaixa = useMemo(
    () => resolverNamespaceEmpresa(fluxoCaixaPorEmpresa, slugEmp, fluxoCaixaLegado, EMPTY_FLUXO_CAIXA),
    [fluxoCaixaPorEmpresa, slugEmp, fluxoCaixaLegado]
  );
  const setFluxoCaixa = (valorOuFn) => {
    if (!setFluxoCaixaPorEmpresa) { setFluxoCaixaLegado?.(valorOuFn); return; }
    setFluxoCaixaPorEmpresa(prevMapa => {
      const mapa = prevMapa || {};
      const atual = resolverNamespaceEmpresa(mapa, slugEmp, fluxoCaixaLegado, EMPTY_FLUXO_CAIXA);
      const novo = typeof valorOuFn === 'function' ? valorOuFn(atual) : valorOuFn;
      return { ...mapa, [slugEmp]: novo };
    });
  };

  const contasReceber = useMemo(
    () => resolverNamespaceEmpresa(contasReceberPorEmpresa, slugEmp, contasReceberLegado, EMPTY_ARR),
    [contasReceberPorEmpresa, slugEmp, contasReceberLegado]
  );
  const setContasReceber = (valorOuFn) => {
    if (!setContasReceberPorEmpresa) { setContasReceberLegado?.(valorOuFn); return; }
    setContasReceberPorEmpresa(prevMapa => {
      const mapa = prevMapa || {};
      const atual = resolverNamespaceEmpresa(mapa, slugEmp, contasReceberLegado, EMPTY_ARR);
      const novo = typeof valorOuFn === 'function' ? valorOuFn(atual) : valorOuFn;
      return { ...mapa, [slugEmp]: novo };
    });
  };

  // Mensalidades: { [clienteId]: { [ano]: { [mes 1-12]: { valor, status } } } }
  // status ∈ 'pendente' | 'pago' | 'atrasado'
  const mensalidades = useMemo(
    () => resolverNamespaceEmpresa(mensalidadesPorEmpresa, slugEmp, null, EMPTY_OBJ),
    [mensalidadesPorEmpresa, slugEmp]
  );
  const setMensalidades = (valorOuFn) => {
    if (!setMensalidadesPorEmpresa) return;
    setMensalidadesPorEmpresa(prevMapa => {
      const mapa = prevMapa || {};
      const atual = resolverNamespaceEmpresa(mapa, slugEmp, null, EMPTY_OBJ);
      const novo = typeof valorOuFn === 'function' ? valorOuFn(atual) : valorOuFn;
      return { ...mapa, [slugEmp]: novo };
    });
  };

  // Orçamentos KzeroSystem (aba Orçamentos): { catalogo: [modulos], orcamentos: [] } por empresa
  const orcamentosKzs = useMemo(
    () => resolverNamespaceEmpresa(orcamentosKzsPorEmpresa, slugEmp, null, EMPTY_ARR),
    [orcamentosKzsPorEmpresa, slugEmp]
  );
  const setOrcamentosKzs = (valorOuFn) => {
    if (!setOrcamentosKzsPorEmpresa) {
      toast.erro('Não foi possível salvar: o Financeiro não recebeu o gravador de orçamentos. Recarregue a página.');
      return;
    }
    setOrcamentosKzsPorEmpresa(prevMapa => {
      const mapa = prevMapa || {};
      const atual = resolverNamespaceEmpresa(mapa, slugEmp, null, EMPTY_ARR);
      const novo = typeof valorOuFn === 'function' ? valorOuFn(atual) : valorOuFn;
      return { ...mapa, [slugEmp]: novo };
    });
  };
  // Catálogo de módulos KzeroSystem — estrutura vem sempre do SEED (fonte da
  // verdade em modulos-kzerosystem.js), preços vêm do BANCO por empresa
  // (catalogoPrecosKzsPorEmpresa). Desde 16/10/2026 o salvamento é automático,
  // 2,5 s depois da última alteração; antes os preços ficavam só no navegador
  // até alguém clicar em "Salvar catálogo", o que nunca tinha acontecido.
  // O localStorage só é LIDO, uma vez, para subir preços antigos ao banco.
  const precosLsKey = `kzerodiary:catalogo-precos-kzs:${slugEmp}`;
  const [precosCatalogo, setPrecosCatalogo] = useState({});
  // Reload do mapa de preços + recuperação agressiva de formatos antigos.
  // Varre TODAS as chaves do localStorage relacionadas ao catálogo (não só
  // a versão canônica) — cobre casos onde slugEmp mudou entre sessões, ou
  // onde o formato antigo ficou órfão em outra empresa da mesma máquina.
  useEffect(() => {
    if (typeof localStorage === 'undefined') return;

    // 1. Lê chave nova (formato atual: mapa {slug: valor}) da empresa ATUAL
    let mapaNovo = {};
    try {
      const raw = localStorage.getItem(precosLsKey);
      if (raw) mapaNovo = JSON.parse(raw) || {};
    } catch {}

    // 2. Varre TODAS as chaves de catálogo no localStorage — recupera preços
    // de qualquer formato/empresa que ainda esteja lá.
    const chavesEncontradas = [];
    const precosRecuperados = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        // Formato antigo: catálogo array (kzerodiary:catalogo-modulos-kzs:xxx)
        if (key.startsWith('kzerodiary:catalogo-modulos-kzs:')) {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            const cat = JSON.parse(raw);
            if (Array.isArray(cat) && cat.length > 0) {
              const mapaExtraido = extrairMapaPrecos(cat);
              Object.assign(precosRecuperados, mapaExtraido);
              chavesEncontradas.push(key);
            }
          } catch {}
        }
        // Formato novo em OUTRA empresa (kzerodiary:catalogo-precos-kzs:xxx
        // diferente da atual) — recupera também, mesma máquina/browser
        if (key.startsWith('kzerodiary:catalogo-precos-kzs:') && key !== precosLsKey) {
          try {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            const mapa = JSON.parse(raw);
            if (mapa && typeof mapa === 'object' && Object.keys(mapa).length > 0) {
              Object.assign(precosRecuperados, mapa);
              chavesEncontradas.push(key);
            }
          } catch {}
        }
      }
    } catch {}

    // 3. Preços do navegador entram só onde o banco não tem valor: o banco
    //    sempre vence. O que sobrar diferente do banco o salvamento automático
    //    envia — é assim que os preços presos no navegador sobem uma única vez.
    //    Só enquanto o banco não tem preço nenhum desta empresa: depois disso o
    //    navegador é ignorado, senão um preço apagado voltaria do cache antigo.
    //    Troca de empresa SUBSTITUI o mapa (nunca mistura preços de empresas).
    const mapaLocal = { ...precosRecuperados, ...mapaNovo };
    const bancoVazio = Object.keys(precosSalvosNoBanco).length === 0;
    setPrecosCatalogo(bancoVazio ? mapaLocal : precosSalvosNoBanco);
    if (bancoVazio && Object.keys(mapaLocal).length > 0) {
      // eslint-disable-next-line no-console
      console.info('[catálogo] Preços do navegador enviados ao banco:', { chavesEncontradas, mapaLocal });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugEmp]);
  // Preços salvos no BANCO (por empresa). Fonte da verdade entre navegadores,
  // máquinas e usuários — o localStorage sozinho fazia o catálogo "sumir"
  // sempre que se trocava de browser ou se limpava o cache.
  const precosSalvosNoBanco = useMemo(
    () => resolverNamespaceEmpresa(catalogoPrecosKzsPorEmpresa, slugEmp, null, EMPTY_OBJ) || EMPTY_OBJ,
    [catalogoPrecosKzsPorEmpresa, slugEmp]
  );
  // O banco chegou (ou mudou por outro usuário)? Ele vence o que está na tela.
  useEffect(() => {
    if (Object.keys(precosSalvosNoBanco).length === 0) return;
    setPrecosCatalogo(prev => ({ ...prev, ...precosSalvosNoBanco }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precosSalvosNoBanco]);

  // Catálogo renderizável = SEED × preços em edição
  const catalogoModulosKzs = useMemo(() => mergeCatalogoComPrecos(precosCatalogo), [precosCatalogo]);
  // Setter compatível com API anterior: recebe catálogo cheio, extrai só os preços.
  // Só atualiza a tela; o salvamento automático abaixo leva ao banco.
  const setCatalogoModulosKzs = (valorOuFn) => {
    const novoCatalogo = typeof valorOuFn === 'function' ? valorOuFn(catalogoModulosKzs) : valorOuFn;
    setPrecosCatalogo(extrairMapaPrecos(novoCatalogo));
  };

  // Há preço em edição que ainda não foi para o banco?
  const catalogoTemMudancaNaoSalva = useMemo(() => {
    const chaves = new Set([...Object.keys(precosCatalogo || {}), ...Object.keys(precosSalvosNoBanco || {})]);
    for (const k of chaves) {
      if ((precosCatalogo?.[k] ?? null) !== (precosSalvosNoBanco?.[k] ?? null)) return true;
    }
    return false;
  }, [precosCatalogo, precosSalvosNoBanco]);

  // Salvamento automático no banco, 2,5 s depois da última alteração. O atraso
  // agrupa a digitação: mandar a cada tecla foi o que derrubou o Supabase com 504.
  useEffect(() => {
    if (!catalogoTemMudancaNaoSalva || !setCatalogoPrecosKzsPorEmpresa) return;
    const t = setTimeout(() => {
      setCatalogoPrecosKzsPorEmpresa(prevMapa => ({ ...(prevMapa || {}), [slugEmp]: precosCatalogo }));
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precosCatalogo, catalogoTemMudancaNaoSalva, slugEmp]);

  // Itens padrão de "Outros" no orçamento (30/10/2026): cadastrados no
  // Catálogo, por empresa. Lista pequena, gravada inteira pelo save genérico.
  const itensPadraoOrc = useMemo(
    () => resolverNamespaceEmpresa(itensPadraoOrcKzsPorEmpresa, slugEmp, null, EMPTY_ARR) || EMPTY_ARR,
    [itensPadraoOrcKzsPorEmpresa, slugEmp]
  );
  const setItensPadraoOrc = (fn) => {
    if (!setItensPadraoOrcKzsPorEmpresa) return;
    setItensPadraoOrcKzsPorEmpresa(prevMapa => {
      const atual = resolverNamespaceEmpresa(prevMapa, slugEmp, null, EMPTY_ARR) || EMPTY_ARR;
      return { ...(prevMapa || {}), [slugEmp]: fn(atual) };
    });
  };
  const addItemPadraoCadastro = () =>
    setItensPadraoOrc(lista => [...lista, { id: `ip-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, descricao: '', valor: '', mensal: false }]);
  const updateItemPadraoCadastro = (id, campo, valor) =>
    setItensPadraoOrc(lista => lista.map(p => p.id === id ? { ...p, [campo]: valor } : p));
  const removeItemPadraoCadastro = (id) =>
    setItensPadraoOrc(lista => lista.filter(p => p.id !== id));

  // Resolveds dos campos com useState local (sincronizados via useEffect abaixo).
  // categorias usa CAT_PADRAO como vazio para que empresas novas comecem com a
  // lista padrão; demais usam EMPTY_ARR / EMPTY_OBJ.
  const categoriasResolved = useMemo(
    () => resolverNamespaceEmpresa(categoriasPorEmpresa, slugEmp, catProp, CAT_PADRAO),
    [categoriasPorEmpresa, slugEmp, catProp]
  );
  const recorrenciasResolved = useMemo(
    () => resolverNamespaceEmpresa(recorrenciasPorEmpresa, slugEmp, recProp, EMPTY_ARR),
    [recorrenciasPorEmpresa, slugEmp, recProp]
  );
  const quotasResolved = useMemo(
    () => resolverNamespaceEmpresa(quotasPorEmpresa, slugEmp, quotasProp, EMPTY_ARR),
    [quotasPorEmpresa, slugEmp, quotasProp]
  );
  const acertosResolved = useMemo(
    () => resolverNamespaceEmpresa(acertosDivPorEmpresa, slugEmp, acertosProp, EMPTY_OBJ),
    [acertosDivPorEmpresa, slugEmp, acertosProp]
  );
  const contasPagarResolved = useMemo(
    () => resolverNamespaceEmpresa(contasPagarPorEmpresa, slugEmp, cpProp, EMPTY_ARR),
    [contasPagarPorEmpresa, slugEmp, cpProp]
  );

  // Reembolsos: leitura apenas (CRUD vive em Documentos). Namespaced por empresa
  // para que a Divisão de Lucros calcule "reembolsos a receber" só dos da empresa ativa.
  const reembolsos = useMemo(
    () => resolverNamespaceEmpresa(reembolsosPorEmpresa, slugEmp, reembolsosLegado, EMPTY_ARR),
    [reembolsosPorEmpresa, slugEmp, reembolsosLegado]
  );

  // ── Estados Fluxo de Caixa ──────────────────────────────────────────────
  const [form, setForm] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroContabil, setFiltroContabil] = useState(''); // '' | 'classificado' | 'pendente'
  const [filtroMes, setFiltroMes] = useState(mesAtualStr());
  const [anexosAbertoId, setAnexosAbertoId] = useState(null);

  // ── Estados Mensalidades ────────────────────────────────────────────────
  const [anoMensalidade, setAnoMensalidade] = useState(() => new Date().getFullYear());
  // Filtro: 'todas' (default) ou 'a_cobrar' (só clientes com algum valor > 0 no ano)
  const [filtroMensalidades, setFiltroMensalidades] = useState('todas');

  // ── Estados Contas a Receber ────────────────────────────────────────────
  const [formCR, setFormCR] = useState(null);
  const [filtroCRStatus, setFiltroCRStatus] = useState('');
  const [filtroCRMes, setFiltroCRMes] = useState('');
  const [recebendoId, setRecebendoId] = useState(null);
  const [dataRecebimento, setDataRecebimento] = useState(hoje());

  // ── Estados Categorias ──────────────────────────────────────────────────
  // Inicializa com o resolved namespaced; se nada resolver (CAT_PADRAO),
  // tenta o cache do localStorage (fallback offline retrocompat).
  const [categorias, setCatLocal] = useState(() => {
    if (categoriasResolved !== CAT_PADRAO) return categoriasResolved;
    const s = localStorage.getItem('kzerodiary_categorias');
    return s ? JSON.parse(s) : CAT_PADRAO;
  });
  const [novaCategoria, setNovaCategoria] = useState('');

  // ── Estados Recorrências ────────────────────────────────────────────────
  const [recorrencias, setRecLocal] = useState(() => {
    if (recorrenciasResolved !== EMPTY_ARR) return recorrenciasResolved;
    const s = localStorage.getItem('kzerodiary_recorrencias');
    return s ? JSON.parse(s) : [];
  });
  const [formRec, setFormRec] = useState(null);

  // ── Estados Orçamentos KzeroSystem ──────────────────────────────────────
  // subAbaOrc: 'catalogo' (config de módulos+preços) ou 'lista' (orçamentos gerados).
  // formOrc: quando != null, mostra modal criar/editar orçamento. Estrutura:
  //   { id, cliente, clienteId, numero, data, validade, status, numEmpresas, itens: [{id, nome, descricao, valorUnitario, descontoPct, selecionado, submodulos: [{id, nome, incluido}]}], observacoes, condicoesEspeciais }
  const [subAbaOrc, setSubAbaOrc] = useState('lista');
  const [formOrc, setFormOrc] = useState(null);
  const [previewOrc, setPreviewOrc] = useState(null);
  // Confirmação de orçamento salvo. Fica no centro da tela em vez de num toast
  // de canto, que passava despercebido — a Bianca salvava e não tinha certeza
  // se tinha gravado.
  const [orcSalvoAviso, setOrcSalvoAviso] = useState(null);
  // Slugs de módulos expandidos no catálogo e no form de orçamento
  const [catalogoExpanded, setCatalogoExpanded] = useState(new Set());
  const [formOrcExpanded, setFormOrcExpanded] = useState(new Set());

  // ── Estados Divisão de Lucros ───────────────────────────────────────────
  const [divMesCompetencia, setDivMesCompetencia] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [quotas, setQuotasLocal] = useState(() => {
    if (quotasResolved !== EMPTY_ARR) return quotasResolved;
    const s = localStorage.getItem('kzerodiary_quotas');
    return s ? JSON.parse(s) : [];
  });
  const [formQuota, setFormQuota] = useState(null);
  const [usarAgendaEstimativa, setUsarAgendaEstimativa] = useState(false);
  // acertosDiv: { "YYYY-MM": { quotaId: { acertos:[{id,descricao,valor}], gastosPessoais:[{id,descricao,valor}], valorPago:number|null } } }
  const [acertosDiv, setAcertosDivLocal] = useState(() => acertosResolved);
  const [expandidoId, setExpandidoId] = useState(null); // id da quota com painel de acertos aberto
  const [formAcerto, setFormAcerto] = useState(null); // { quotaId, descricao, valor, id? }
  const [formGasto, setFormGasto] = useState(null); // { quotaId, descricao, valor, id? }
  const [divMesDespesas, setDivMesDespesas] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });

  // ── Estados Contas a Pagar ──────────────────────────────────────────────
  const [contasPagar, setCpLocal] = useState(() => contasPagarResolved);
  const [formCP, setFormCP] = useState(null);
  const [editandoCpId, setEditandoCpId] = useState(null);
  const [filtroCPMes, setFiltroCPMes] = useState(mesAtualStr());
  const [filtroCPStatus, setFiltroCPStatus] = useState('');
  const [saldoInicial, setSaldoInicial] = useState(() => { const sv = localStorage.getItem('kzerodiary_saldo_inicial'); return sv ? parseFloat(sv) : 0; });
  const [editandoSaldo, setEditandoSaldo] = useState(false);
  const [saldoTemp, setSaldoTemp] = useState('');
  const [mostrarDespDiv, setMostrarDespDiv] = useState(false);

  // Sync resolved → state quando Supabase carrega ou troca de empresa
  useEffect(() => { setCatLocal(categoriasResolved); }, [categoriasResolved]);
  useEffect(() => { setRecLocal(recorrenciasResolved); }, [recorrenciasResolved]);
  useEffect(() => { setQuotasLocal(quotasResolved); }, [quotasResolved]);
  useEffect(() => { setAcertosDivLocal(acertosResolved); }, [acertosResolved]);
  useEffect(() => { setCpLocal(contasPagarResolved); }, [contasPagarResolved]);

  // Setters: gravam no mapa *PorEmpresa quando disponível; caem no setter legado
  // para retrocompat; usam localStorage como fallback offline (categorias/recorrencias/quotas).
  const setCategorias = v => {
    const next = typeof v === 'function' ? v(categorias) : v;
    setCatLocal(next);
    if (setCategoriasPorEmpresa) {
      setCategoriasPorEmpresa(prev => ({ ...(prev || {}), [slugEmp]: next }));
    } else if (setCatProp) {
      setCatProp(next);
    } else {
      try { localStorage.setItem('kzerodiary_categorias', JSON.stringify(next)); } catch { /* quota */ }
    }
  };
  const setRecorrencias = v => {
    const next = typeof v === 'function' ? v(recorrencias) : v;
    setRecLocal(next);
    if (setRecorrenciasPorEmpresa) {
      setRecorrenciasPorEmpresa(prev => ({ ...(prev || {}), [slugEmp]: next }));
    } else if (setRecProp) {
      setRecProp(next);
    } else {
      try { localStorage.setItem('kzerodiary_recorrencias', JSON.stringify(next)); } catch { /* quota */ }
    }
  };
  const setQuotas = v => {
    const next = typeof v === 'function' ? v(quotas) : v;
    setQuotasLocal(next);
    if (setQuotasPorEmpresa) {
      setQuotasPorEmpresa(prev => ({ ...(prev || {}), [slugEmp]: next }));
    } else if (setQuotasProp) {
      setQuotasProp(next);
    } else {
      try { localStorage.setItem('kzerodiary_quotas', JSON.stringify(next)); } catch { /* quota */ }
    }
  };
  const setAcertosDiv = v => {
    const next = typeof v === 'function' ? v(acertosDiv) : v;
    setAcertosDivLocal(next);
    if (setAcertosDivPorEmpresa) {
      setAcertosDivPorEmpresa(prev => ({ ...(prev || {}), [slugEmp]: next }));
    } else if (setAcertosProp) {
      setAcertosProp(next);
    }
  };
  const setContasPagar = v => {
    const next = typeof v === 'function' ? v(contasPagar) : v;
    setCpLocal(next);
    if (setContasPagarPorEmpresa) {
      setContasPagarPorEmpresa(prev => ({ ...(prev || {}), [slugEmp]: next }));
    } else if (setCpProp) {
      setCpProp(next);
    }
  };

  // Helpers de acertos
  const getMesAcertos = (quotaId) => acertosDiv[divMesCompetencia]?.[quotaId] ?? { acertos: [], valorPago: null };
  const setMesAcertos = (quotaId, data) => setAcertosDiv(prev => ({ ...prev, [divMesCompetencia]: { ...(prev[divMesCompetencia] ?? {}), [quotaId]: { ...(prev[divMesCompetencia]?.[quotaId] ?? { acertos: [] }), ...data } } }));

  const salvarAcerto = () => {
    if (!formAcerto?.descricao || formAcerto.valor === '') { toast.aviso('Preencha descrição e valor.'); return; }
    const { quotaId, descricao, valor, id } = formAcerto;
    const ma = getMesAcertos(quotaId);
    const item = { id: id ?? Date.now(), descricao, valor: parseFloat(valor) };
    const acertos = id ? ma.acertos.map(a => a.id === id ? item : a) : [...ma.acertos, item];
    setMesAcertos(quotaId, { acertos });
    setFormAcerto(null);
  };

  const excluirAcerto = (quotaId, acertoId) => {
    const ma = getMesAcertos(quotaId);
    setMesAcertos(quotaId, { acertos: ma.acertos.filter(a => a.id !== acertoId) });
  };

  const salvarGasto = () => {
    if (!formGasto?.descricao || formGasto.valor === '') { toast.aviso('Preencha descrição e valor.'); return; }
    const { quotaId, descricao, valor, id, tipo } = formGasto;
    const ma = getMesAcertos(quotaId);
    const item = { id: id ?? Date.now(), descricao, valor: Math.abs(parseFloat(valor)), tipo: tipo === 'receita' ? 'receita' : 'gasto' };
    const gastosPessoais = id
      ? (ma.gastosPessoais || []).map(g => g.id === id ? item : g)
      : [...(ma.gastosPessoais || []), item];
    setMesAcertos(quotaId, { gastosPessoais });
    setFormGasto(null);
  };

  const excluirGasto = (quotaId, gastoId) => {
    const ma = getMesAcertos(quotaId);
    setMesAcertos(quotaId, { gastosPessoais: (ma.gastosPessoais || []).filter(g => g.id !== gastoId) });
  };

  const setValorPago = (quotaId, valor) => setMesAcertos(quotaId, { valorPago: valor === '' ? null : parseFloat(valor) });

  // Limpar duplicatas no fluxoCaixa (executa 1x)
  const dedupDone = useRef(false);
  useEffect(() => {
    if (dedupDone.current || !fluxoCaixa || fluxoCaixa.length === 0) return;
    dedupDone.current = true;
    const visto = new Set();
    let removidos = 0;
    const limpo = fluxoCaixa.filter(f => {
      const chave = f.data + '_' + parseFloat(f.valor || 0) + '_' + (f.descricao || '');
      if (visto.has(chave)) { removidos++; return false; }
      visto.add(chave);
      return true;
    });
    if (removidos > 0) {
      setFluxoCaixa(limpo);
      toast.sucesso(`${removidos} duplicata(s) removida(s) do fluxo de caixa.`);
    }
  }, [fluxoCaixa]); // eslint-disable-line react-hooks/exhaustive-deps

  // ══════════════════════════════════════════════════════════════════════════
  // CONTAS A RECEBER
  // ══════════════════════════════════════════════════════════════════════════

  const statusConta = (cr) => {
    if (cr.status === 'recebido') return 'recebido';
    if (cr.dataVencimento < hoje()) return 'atrasado';
    return 'aberto';
  };

  const gerarContasMesAnterior = () => {
    const agora = new Date();
    const anoMesAnt = agora.getMonth() === 0 ? agora.getFullYear() - 1 : agora.getFullYear();
    const mesAnt = agora.getMonth() === 0 ? 12 : agora.getMonth();
    const mesCompetencia = `${anoMesAnt}-${String(mesAnt).padStart(2, '0')}`;
    const inicioMesAnt = new Date(anoMesAnt, mesAnt - 1, 1);
    const fimMesAnt = new Date(agora.getFullYear(), agora.getMonth(), 1);

    // IDs de agendamentos já linkados em contas existentes
    const agendsUsados = new Set(contasReceber.flatMap(cr => cr.agendamentoIds || []));

    const agendsFiltrados = agendamentos.filter(a => {
      const dt = new Date(a.data);
      return dt >= inicioMesAnt && dt < fimMesAnt && a.tipo !== 'PESSOAL' && !agendsUsados.has(a.id);
    });

    if (agendsFiltrados.length === 0) {
      toast.aviso('Nenhum agendamento do mês anterior sem conta a receber.');
      return;
    }

    const vencimento = quintoDiaUtil(agora.getFullYear(), agora.getMonth() + 1);

    // Agrupa por cliente
    const porCliente = {};
    agendsFiltrados.forEach(a => {
      if (!porCliente[a.cliente]) porCliente[a.cliente] = { valor: 0, agendamentoIds: [] };
      porCliente[a.cliente].valor += parseFloat(a.valor || 0);
      porCliente[a.cliente].agendamentoIds.push(a.id);
    });

    const novas = Object.entries(porCliente).map(([clienteId, dados]) => {
      const cliente = clientes.find(c => String(c.id) === String(clienteId));
      const nome = cliente?.nome || 'Cliente';
      return {
        id: Date.now() + Math.random(),
        clienteId,
        clienteNome: nome,
        descricao: `Honorários ${fmtMes(mesCompetencia)} – ${nome}`,
        valor: dados.valor.toFixed(2),
        mesCompetencia,
        dataVencimento: vencimento,
        status: 'aberto',
        dataRecebimento: null,
        fluxoCaixaId: null,
        agendamentoIds: dados.agendamentoIds,
      };
    });

    setContasReceber(prev => [...prev, ...novas]);
    toast.sucesso(`${novas.length} conta(s) a receber gerada(s) — vencimento ${new Date(vencimento + 'T00:00').toLocaleDateString('pt-BR')}`);
  };

  const salvarContaManual = () => {
    if (!formCR.clienteId || !formCR.valor || !formCR.descricao || !formCR.dataVencimento) {
      toast.aviso('Preencha cliente, descrição, valor e vencimento.');
      return;
    }
    const cliente = clientes.find(c => String(c.id) === String(formCR.clienteId));
    const nova = {
      id: Date.now() + Math.random(),
      clienteId: formCR.clienteId,
      clienteNome: cliente?.nome || 'Cliente',
      descricao: formCR.descricao,
      valor: formCR.valor,
      mesCompetencia: formCR.mesCompetencia || '',
      dataVencimento: formCR.dataVencimento,
      status: 'aberto',
      dataRecebimento: null,
      fluxoCaixaId: null,
      agendamentoIds: [],
    };
    setContasReceber(prev => [...prev, nova]);
    setFormCR(null);
    toast.sucesso('Conta a receber criada!');
  };

  const registrarRecebimento = (cr) => {
    const novoFcId = Date.now() + Math.random();
    const lancamento = {
      id: novoFcId,
      data: dataRecebimento,
      tipo: 'RECEITA',
      categoria: 'Recebimentos',
      descricao: cr.descricao,
      valor: cr.valor,
      mesCompetencia: cr.mesCompetencia || '',
      clienteNome: cr.clienteNome,
      clienteId: cr.clienteId,
      contaReceberId: cr.id,
    };
    setFluxoCaixa(prev => [...prev, lancamento]);
    setContasReceber(prev => prev.map(c => c.id === cr.id ? { ...c, status: 'recebido', dataRecebimento, fluxoCaixaId: novoFcId } : c));
    setRecebendoId(null);
    toast.sucesso('Recebimento registrado e lançado no fluxo de caixa!');
  };

  const desfazerRecebimento = (cr) => {
    if (!window.confirm('Desfazer recebimento? O lançamento no fluxo de caixa será removido.')) return;
    setFluxoCaixa(prev => prev.filter(f => f.id !== cr.fluxoCaixaId));
    setContasReceber(prev => prev.map(c => c.id === cr.id ? { ...c, status: 'aberto', dataRecebimento: null, fluxoCaixaId: null } : c));
    toast.sucesso('Recebimento desfeito.');
  };

  const excluirConta = (id) => {
    if (!window.confirm('Excluir conta a receber?')) return;
    setContasReceber(prev => prev.filter(c => c.id !== id));
    toast.sucesso('Conta removida.');
  };

  const contasFiltradas = useMemo(() => {
    return contasReceber.filter(cr => {
      const st = statusConta(cr);
      if (filtroCRStatus && st !== filtroCRStatus) return false;
      if (filtroCRMes && cr.mesCompetencia !== filtroCRMes) return false;
      return true;
    }).sort((a, b) => a.dataVencimento.localeCompare(b.dataVencimento));
  }, [contasReceber, filtroCRStatus, filtroCRMes]); // eslint-disable-line

  const resumoCR = useMemo(() => {
    const mesHoje = mesAtualStr();
    let aReceber = 0, emAtraso = 0, recebidoMes = 0;
    contasReceber.forEach(cr => {
      const st = statusConta(cr);
      if (st === 'aberto') aReceber += parseFloat(cr.valor || 0);
      else if (st === 'atrasado') emAtraso += parseFloat(cr.valor || 0);
      else if (st === 'recebido' && (cr.dataRecebimento || '').startsWith(mesHoje)) recebidoMes += parseFloat(cr.valor || 0);
    });
    return { aReceber, emAtraso, recebidoMes };
  }, [contasReceber]); // eslint-disable-line

  // ══════════════════════════════════════════════════════════════════════════
  // FLUXO DE CAIXA
  // ══════════════════════════════════════════════════════════════════════════

  const fluxoFiltrado = useMemo(() => {
    return fluxoCaixa.filter(f => {
      if (filtroTipo && f.tipo !== filtroTipo) return false;
      if (filtroCategoria && f.categoria !== filtroCategoria) return false;
      if (filtroMes && !(f.data || '').startsWith(filtroMes)) return false;
      if (filtroContabil === 'classificado' && !f.contaContabil) return false;
      if (filtroContabil === 'pendente' && f.contaContabil) return false;
      return true;
    }).sort((a, b) => (b.data || '').localeCompare(a.data || ''));
  }, [fluxoCaixa, filtroTipo, filtroCategoria, filtroContabil, filtroMes]);

  const previstoCR = useMemo(() => {
    if (!filtroMes) return 0;
    return contasReceber
      .filter(cr => (cr.dataVencimento || '').startsWith(filtroMes))
      .reduce((s, cr) => s + parseFloat(cr.valor || 0), 0);
  }, [contasReceber, filtroMes]);

  const realizadoMes = fluxoFiltrado.filter(f => f.tipo === 'RECEITA').reduce((s, f) => s + parseFloat(f.valor || 0), 0);
  const despesasMes = fluxoFiltrado.filter(f => f.tipo === 'DESPESA').reduce((s, f) => s + parseFloat(f.valor || 0), 0);
  const saldoMes = realizadoMes - despesasMes;

  const salvarLancamento = () => {
    if (!form.descricao || !form.valor) { toast.aviso('Preencha descrição e valor.'); return; }
    if (editandoId) {
      // Spread do original antes do form: preserva anexos, origem, status, etc.
      setFluxoCaixa(fluxoCaixa.map(f => f.id === editandoId ? { ...f, ...form, id: editandoId } : f));
      toast.sucesso('Lançamento atualizado!');
      setEditandoId(null);
    } else {
      setFluxoCaixa([...fluxoCaixa, { ...form, id: Date.now() }]);
      toast.sucesso('Lançamento adicionado!');
    }
    setForm(null);
  };

  // Atualiza só o array `anexos` de um lançamento, sem mexer no resto.
  const setAnexosLancamento = (lancId, novosAnexos) => {
    setFluxoCaixa(prev => prev.map(f => f.id === lancId ? { ...f, anexos: novosAnexos } : f));
  };

  const editarLancamento = (fc) => {
    setEditandoId(fc.id);
    setForm({ tipo: fc.tipo, categoria: fc.categoria, descricao: fc.descricao, valor: fc.valor, data: fc.data });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const deletarLancamento = (id) => {
    const lanc = fluxoCaixa.find(f => f.id === id);
    if (lanc?.origem === 'extrato') {
      toast.aviso('Lançamentos importados do extrato bancário não podem ser excluídos.');
      return;
    }
    if (window.confirm('Excluir lançamento?')) {
      setFluxoCaixa(fluxoCaixa.filter(f => f.id !== id));
      toast.sucesso('Lançamento excluído.');
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // DIVISÃO DE LUCROS
  // ══════════════════════════════════════════════════════════════════════════

  const divResultado = useMemo(() => {
    const receitasReais = fluxoCaixa
      .filter(f => {
        if (f.tipo !== 'RECEITA') return false;
        const ref = f.mesCompetencia || (f.data || '').substring(0, 7);
        return ref === divMesCompetencia;
      })
      .reduce((s, f) => s + parseFloat(f.valor || 0), 0);

    const receitasAgenda = agendamentos
      .filter(ag => ag.tipo !== 'PESSOAL' && (ag.data || '').startsWith(divMesCompetencia) && parseFloat(ag.valor || 0) > 0)
      .reduce((s, ag) => s + parseFloat(ag.valor || 0), 0);

    // Reembolsos a receber do mês (PENDENTE ou SOLICITADO), pela data do lançamento
    const reembolsosAReceber = reembolsos
      .filter(r => {
        const st = r.status || 'PENDENTE';
        const mesR = (r.data || '').substring(0, 7);
        return (st === 'PENDENTE' || st === 'SOLICITADO') && mesR === divMesCompetencia;
      })
      .reduce((s, r) => s + parseFloat(r.valor || 0), 0);

    // Acumulados a Receber: contasReceber abertas ou atrasadas do mês de competência
    const acumuladosAReceber = contasReceber
      .filter(cr => {
        const st = cr.status === 'recebido' ? 'recebido' : (cr.dataVencimento < new Date().toISOString().slice(0, 10) ? 'atrasado' : 'aberto');
        return st !== 'recebido' && cr.mesCompetencia === divMesCompetencia;
      })
      .reduce((s, cr) => s + parseFloat(cr.valor || 0), 0);

    const receitas = usarAgendaEstimativa ? receitasAgenda : receitasReais;
    const isEstimativa = usarAgendaEstimativa;

    const despesasLista = fluxoCaixa
      .filter(f => (f.tipo || '').toUpperCase() !== 'RECEITA' && (f.data || '').startsWith(divMesDespesas)
        && !(f.categoria || '').toLowerCase().includes('antecipa') && f.status !== 'rejeitado');
    const despesas = despesasLista.reduce((s, f) => s + parseFloat(f.valor || 0), 0);

    // Total de acertos já lançados para o mês (afeta o lucro disponível a distribuir)
    const totalAcertosGlobal = quotas.reduce((s, q) => {
      const ma = acertosDiv[divMesCompetencia]?.[q.id] ?? { acertos: [] };
      return s + (ma.acertos || []).reduce((sa, a) => sa + parseFloat(a.valor || 0), 0);
    }, 0);

    // Receitas + Acumulados a Receber entram na divisão; Reembolsos NÃO
    const receitaTotal = receitas + acumuladosAReceber;
    const lucroLiquido = receitaTotal - despesas;
    const lucroDisponivel = lucroLiquido - totalAcertosGlobal;

    const calculadas = quotas.map(q => {
      const base = q.tipo === 'percentual'
        ? (lucroDisponivel * parseFloat(q.valor || 0)) / 100
        : parseFloat(q.valor || 0);
      const ma = acertosDiv[divMesCompetencia]?.[q.id] ?? { acertos: [], gastosPessoais: [], valorPago: null };
      const totalAcertos = (ma.acertos || []).reduce((s, a) => s + parseFloat(a.valor || 0), 0);
      const valorComAcertos = base + totalAcertos;
      const itensPessoais = ma.gastosPessoais || [];
      const totalGastosPessoais = itensPessoais
        .filter(g => g.tipo !== 'receita')
        .reduce((s, g) => s + Math.abs(parseFloat(g.valor || 0)), 0);
      const totalReceitasPessoais = itensPessoais
        .filter(g => g.tipo === 'receita')
        .reduce((s, g) => s + Math.abs(parseFloat(g.valor || 0)), 0);
      const valorLiquido = valorComAcertos - totalGastosPessoais + totalReceitasPessoais;
      const valorPago = ma.valorPago !== null && ma.valorPago !== undefined ? ma.valorPago : null;
      const faltante = valorPago !== null ? valorLiquido - valorPago : 0;
      return { ...q, base, acertos: ma.acertos || [], totalAcertos, valorComAcertos, gastosPessoais: itensPessoais, totalGastosPessoais, totalReceitasPessoais, valorLiquido, valorPago, faltante };
    });
    const totalDistribuido = calculadas.reduce((s, q) => s + q.valorComAcertos, 0);
    const excedente = totalDistribuido > lucroDisponivel && lucroDisponivel > 0;
    // Se o total excede o lucro disponível, escala proporcionalmente cada cota para o saldo ficar em 0
    const fatorEscala = excedente ? lucroDisponivel / totalDistribuido : 1;
    const calculadasFinal = excedente
      ? calculadas.map(q => ({ ...q, valorComAcertos: q.valorComAcertos * fatorEscala, base: q.base * fatorEscala }))
      : calculadas;
    const totalDistribuidoFinal = calculadasFinal.reduce((s, q) => s + q.valorComAcertos, 0);
    const saldoRestante = Math.max(0, lucroDisponivel - totalDistribuidoFinal);
    return { receitas, acumuladosAReceber, reembolsosAReceber, receitaTotal, despesas, despesasLista, lucroLiquido, lucroDisponivel, calculadas: calculadasFinal, totalDistribuido: totalDistribuidoFinal, saldoRestante, excedente, isEstimativa };
  }, [fluxoCaixa, agendamentos, reembolsos, contasReceber, divMesCompetencia, divMesDespesas, quotas, usarAgendaEstimativa, acertosDiv]);

  const salvarQuota = () => {
    if (!formQuota.nome || !formQuota.valor) { toast.aviso('Preencha nome e valor/percentual.'); return; }
    if (formQuota.id) {
      setQuotas(quotas.map(q => q.id === formQuota.id ? formQuota : q));
      toast.sucesso('Quota atualizada!');
    } else {
      setQuotas([...quotas, { ...formQuota, id: Date.now() }]);
      toast.sucesso('Quota adicionada!');
    }
    setFormQuota(null);
  };

  const buildDivisaoHTML = ({ receitas, acumuladosAReceber, reembolsosAReceber, receitaTotal, despesas, lucroLiquido, lucroDisponivel, calculadas, saldoRestante }) => `
    <h2>Competência: ${fmtMes(divMesCompetencia)} · Despesas: ${fmtMes(divMesDespesas)}</h2>
    <table>
      <thead><tr><th>Item</th><th>Valor</th></tr></thead>
      <tbody>
        <tr><td>Receitas de Serviços (ref. ${fmtMes(divMesCompetencia)})</td><td>${fmtMoeda(receitas)}</td></tr>
        ${acumuladosAReceber > 0 ? `<tr><td>Acumulados a Receber (ref. ${fmtMes(divMesCompetencia)})</td><td>${fmtMoeda(acumuladosAReceber)}</td></tr>` : ''}
        ${reembolsosAReceber > 0 ? `<tr><td><em>Reembolsos a Receber (não entra na divisão)</em></td><td><em>${fmtMoeda(reembolsosAReceber)}</em></td></tr>` : ''}
        <tr><td>Despesas (${fmtMes(divMesDespesas)})</td><td>${fmtMoeda(despesas)}</td></tr>
        <tr><td><strong>Lucro Líquido</strong></td><td><strong>${fmtMoeda(lucroLiquido)}</strong></td></tr>
        <tr><td><strong>Lucro Disponível p/ Distribuir</strong></td><td><strong>${fmtMoeda(lucroDisponivel)}</strong></td></tr>
      </tbody>
    </table>
    <h2 style="margin-top:24px;">Divisão de Lucros</h2>
    <table>
      <thead><tr><th>Sócia</th><th>Base (${fmtMes(divMesCompetencia)})</th><th>Acertos</th><th>Com Acertos</th><th>Ajustes Pessoais</th><th>A Receber</th><th>Pago</th><th>Faltante</th></tr></thead>
      <tbody>
        ${calculadas.map(q => {
          const gastos = (q.gastosPessoais || []).filter(g => g.tipo !== 'receita');
          const receitas = (q.gastosPessoais || []).filter(g => g.tipo === 'receita');
          const partes = [];
          if (q.totalGastosPessoais > 0) partes.push(`<span style="color:#f87171">−${fmtMoeda(q.totalGastosPessoais)}</span>`);
          if (q.totalReceitasPessoais > 0) partes.push(`<span style="color:#10b981">+${fmtMoeda(q.totalReceitasPessoais)}</span>`);
          const detalhe = [
            ...gastos.map(g => `<span style="color:#f87171">${g.descricao}: −${fmtMoeda(Math.abs(g.valor))}</span>`),
            ...receitas.map(g => `<span style="color:#10b981">${g.descricao}: +${fmtMoeda(Math.abs(g.valor))}</span>`)
          ].join(', ');
          const colAjustes = partes.length ? `${partes.join(' ')}${detalhe ? '<br/><small>' + detalhe + '</small>' : ''}` : '—';
          return `
          <tr>
            <td><strong>${q.nome}</strong><br/><small>${q.tipo === 'percentual' ? q.valor + '%' : 'Fixo'}</small></td>
            <td>${fmtMoeda(q.base)}</td>
            <td>${fmtMoeda(q.totalAcertos)}${q.acertos.length ? '<br/><small>' + q.acertos.map(a => `${a.descricao}: ${fmtMoeda(a.valor)}`).join(', ') + '</small>' : ''}</td>
            <td>${fmtMoeda(q.valorComAcertos)}</td>
            <td>${colAjustes}</td>
            <td><strong>${fmtMoeda(q.valorLiquido)}</strong></td>
            <td>${q.valorPago !== null ? fmtMoeda(q.valorPago) : '—'}</td>
            <td>${q.faltante !== 0 ? fmtMoeda(q.faltante) : '—'}</td>
          </tr>`;
        }).join('')}
        <tr><td colspan="5"><strong>Saldo Restante</strong></td><td><strong>${fmtMoeda(saldoRestante)}</strong></td><td></td><td></td></tr>
      </tbody>
    </table>
  `;

  const exportarDivisaoPDF = () => { exportarPDF(buildDivisaoHTML(divResultado), `Divisao de Lucros - ${divMesCompetencia}`); };
  const exportarDivisaoWord = () => { exportarWord(buildDivisaoHTML(divResultado), `Divisao de Lucros - ${divMesCompetencia}`); };

  // ── Categorias ──────────────────────────────────────────────────────────
  const adicionarCategoria = () => {
    if (novaCategoria.trim() && !categorias.includes(novaCategoria.trim())) {
      setCategorias([...categorias, novaCategoria.trim()]);
      setNovaCategoria('');
    }
  };

  // ── Recorrências ────────────────────────────────────────────────────────
  // Gera lançamentos para UMA recorrência:
  //  - Com dataFim: gera TODAS as parcelas até dataFim (respeita integralmente)
  //  - Sem dataFim: projeta 12 meses adiante da data atual (recorrência "infinita" tipo aluguel)
  // Limite de segurança: 240 iterações (~20 anos mensal / ~4,6 anos semanal)
  const gerarLancamentosRecorrencia = (rec, datasJaExistentes = new Set()) => {
    const parseLocal = (s) => { const [a,m,d] = s.split('-').map(Number); return new Date(a, m-1, d); };
    const limiteInfinita = new Date();
    limiteInfinita.setMonth(limiteInfinita.getMonth() + 12);
    const limiteFinal = rec.dataFim ? parseLocal(rec.dataFim) : limiteInfinita;
    const novos = [];
    const dt = parseLocal(rec.dataInicio);
    let n = 0;
    const MAX_ITER = 240;

    while (n < MAX_ITER && dt <= limiteFinal) {
      const dataIso = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
      if (!datasJaExistentes.has(dataIso)) {
        novos.push({
          id: Date.now() + n + Math.random(),
          data: dataIso,
          tipo: rec.tipo || 'DESPESA',
          categoria: rec.categoria || 'A Classificar',
          descricao: rec.descricao,
          valor: parseFloat(rec.valor),
          status: 'previsto',
          recorrenteId: rec.id,
        });
      }
      if (rec.frequencia === 'MENSAL') dt.setMonth(dt.getMonth() + 1);
      else if (rec.frequencia === 'SEMANAL') dt.setDate(dt.getDate() + 7);
      else if (rec.frequencia === 'QUINZENAL') dt.setDate(dt.getDate() + 15);
      else break;
      n++;
    }
    return novos;
  };

  // Regenera (completa) os lançamentos faltantes de uma recorrência existente.
  // NÃO remove nem modifica lançamentos já presentes — apenas adiciona datas faltantes.
  const regenerarLancamentosRecorrencia = (rec) => {
    const datasExistentes = new Set(
      fluxoCaixa.filter(l => l.recorrenteId === rec.id).map(l => l.data)
    );
    const faltantes = gerarLancamentosRecorrencia(rec, datasExistentes);
    if (faltantes.length > 0) {
      setFluxoCaixa(prev => [...prev, ...faltantes]);
      toast.sucesso(`${faltantes.length} lançamento(s) adicionado(s) a "${rec.descricao}".`);
    } else {
      toast.info(`"${rec.descricao}" já está com todos os lançamentos projetados.`);
    }
  };

  const salvarRecorrencia = () => {
    if (!formRec.descricao || !formRec.valor) { toast.aviso('Preencha descrição e valor.'); return; }
    const novaRec = { ...formRec, valor: parseFloat(formRec.valor) };
    setRecorrencias([...recorrencias, novaRec]);
    // Gerar lançamentos futuros imediatamente
    const novosLanc = gerarLancamentosRecorrencia(novaRec);
    if (novosLanc.length > 0) {
      setFluxoCaixa(prev => [...prev, ...novosLanc]);
      toast.sucesso(`Recorrência cadastrada! ${novosLanc.length} lançamento(s) projetado(s).`);
    } else {
      toast.sucesso('Recorrência cadastrada!');
    }
    setFormRec(null);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  const abas = [
    ['fluxo', '💸 Fluxo de Caixa'],
    ['mensalidades', '💳 Mensalidades'],
    ['divisao', '💰 Divisão de Lucros'],
    ['categorias', '🏷️ Categorias'],
    ['recorrencias', '🔄 Recorrências'],
    ['orcamentos', '📋 Orçamentos KzeroSystem'],
  ];

  const mudarAba = (id) => {
    setAba(id);
    setForm(null);
    setFormCR(null);
    setFormCP(null);
    setFormRec(null);
    setEditandoId(null);
    setEditandoCpId(null);
    setRecebendoId(null);
  };

  // Badge de status
  const badgeStatus = (st) => {
    const cfg = {
      aberto:   { cor: '#10b981', label: 'Em aberto' },
      atrasado: { cor: '#ef4444', label: 'Atrasado' },
      recebido: { cor: '#5f5874', label: 'Recebido' },
    }[st] || { cor: '#5f5874', label: st };
    return (
      <span style={{ border: `1.5px solid ${cfg.cor}`, color: cfg.cor, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <div className="m-container" style={s.container}>
      <h2 style={s.titulo}>Financeiro</h2>

      {/* Abas */}
      <div className="m-abas" style={s.abas}>
        {abas.map(([id, label]) => (
          <button key={id} style={{ ...s.aba, ...(aba === id ? s.abaAtiva : {}) }} onClick={() => mudarAba(id)}>
            {label}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          ABA: CONTAS A RECEBER (REMOVIDA — dados mantidos para compatibilidade)
      ════════════════════════════════════════════════════════════════════ */}
      {aba === 'contas_DESATIVADA' && (
        <>
          <div className="mobile-grid-1" style={s.resumoGrid}>
            <div style={s.resumoCard}>
              <div style={s.resumoLabel}>A receber</div>
              <div style={{ ...s.resumoValor, color: '#10b981' }}>{fmtMoeda(resumoCR.aReceber)}</div>
            </div>
            <div style={s.resumoCard}>
              <div style={s.resumoLabel}>Em atraso</div>
              <div style={{ ...s.resumoValor, color: '#ef4444' }}>{fmtMoeda(resumoCR.emAtraso)}</div>
            </div>
            <div style={s.resumoCard}>
              <div style={s.resumoLabel}>Recebido este mês</div>
              <div style={{ ...s.resumoValor, color: '#5f5874' }}>{fmtMoeda(resumoCR.recebidoMes)}</div>
            </div>
          </div>

          {/* Ações + Filtros */}
          <div style={{ ...s.filtrosCard, flexWrap: 'wrap', gap: '0.75rem' }}>
            <button style={{ ...s.btnNovo, marginLeft: 0, background: '#10b981' }} onClick={gerarContasMesAnterior}>
              <RefreshCw size={15} /> Gerar do Mês Anterior
            </button>
            <button style={{ ...s.btnNovo, marginLeft: 0 }} onClick={() => setFormCR({ clienteId: '', descricao: '', valor: '', mesCompetencia: '', dataVencimento: hoje() })}>
              <Plus size={15} /> Nova conta a receber
            </button>
            <select style={s.select} value={filtroCRStatus} onChange={e => setFiltroCRStatus(e.target.value)}>
              <option value="">Todos os status</option>
              <option value="aberto">Em aberto</option>
              <option value="atrasado">Atrasado</option>
              <option value="recebido">Recebido</option>
            </select>
            <input type="month" style={s.select} value={filtroCRMes} onChange={e => setFiltroCRMes(e.target.value)} title="Filtrar por mês de competência" />
          </div>

          {/* Form nova conta manual */}
          {formCR && (
            <div style={s.formCard}>
              <h3 style={s.formTitulo}>Nova Conta a Receber</h3>
              <div style={s.formRow}>
                <div style={s.campo}>
                  <label style={s.label}>Cliente *</label>
                  <select style={s.input} value={formCR.clienteId} onChange={e => setFormCR({ ...formCR, clienteId: e.target.value })}>
                    <option value="">Selecione...</option>
                    {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <div style={{ ...s.campo, flex: 2 }}>
                  <label style={s.label}>Descrição *</label>
                  <input type="text" style={s.input} value={formCR.descricao} placeholder="Ex: Honorários março/2026" onChange={e => setFormCR({ ...formCR, descricao: e.target.value })} />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.campo}>
                  <label style={s.label}>Valor (R$) *</label>
                  <input type="number" style={s.input} value={formCR.valor} step="0.01" placeholder="0,00" onChange={e => setFormCR({ ...formCR, valor: e.target.value })} />
                </div>
                <div style={s.campo}>
                  <label style={s.label}>Mês de Competência</label>
                  <input type="month" style={s.input} value={formCR.mesCompetencia} onChange={e => setFormCR({ ...formCR, mesCompetencia: e.target.value })} />
                </div>
                <div style={s.campo}>
                  <label style={s.label}>Vencimento *</label>
                  <input type="date" style={s.input} value={formCR.dataVencimento} onChange={e => setFormCR({ ...formCR, dataVencimento: e.target.value })} />
                </div>
              </div>
              <div style={s.formActions}>
                <button style={s.btnSalvar} onClick={salvarContaManual}>Salvar</button>
                <button style={s.btnCancelar} onClick={() => setFormCR(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Lista de contas */}
          <div className="mobile-table-wrap" style={s.tabelaCard}>
            <table style={s.tabela}>
              <thead>
                <tr>
                  {['Vencimento', 'Cliente', 'Descrição', 'Competência', 'Valor', 'Status', ''].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contasFiltradas.length === 0 ? (
                  <tr><td colSpan={7} style={s.tdVazio}>Nenhuma conta a receber encontrada.</td></tr>
                ) : contasFiltradas.map(cr => {
                  const st = statusConta(cr);
                  const emRecebimento = recebendoId === cr.id;
                  return (
                    <React.Fragment key={cr.id}>
                      <tr style={{ background: emRecebimento ? 'rgba(16,185,129,0.05)' : 'transparent' }}>
                        <td style={s.td}>{cr.dataVencimento ? new Date(cr.dataVencimento + 'T00:00').toLocaleDateString('pt-BR') : '—'}</td>
                        <td style={s.td}>{cr.clienteNome}</td>
                        <td style={s.td}>{cr.descricao}</td>
                        <td style={{ ...s.td, fontSize: 12, color: '#5f5874' }}>{fmtMes(cr.mesCompetencia)}</td>
                        <td style={{ ...s.td, fontWeight: 600, color: '#10b981' }}>{fmtMoeda(cr.valor)}</td>
                        <td style={s.td}>{badgeStatus(st)}</td>
                        <td style={s.td}>
                          <div style={s.acoes}>
                            {(st === 'aberto' || st === 'atrasado') && (
                              <button style={{ ...s.btnEdit, color: '#10b981' }} title="Registrar recebimento" onClick={() => { setRecebendoId(cr.id); setDataRecebimento(hoje()); }}>
                                <CheckCircle size={15} />
                              </button>
                            )}
                            {st === 'recebido' && (
                              <button style={{ ...s.btnEdit, color: '#f59e0b' }} title="Desfazer recebimento" onClick={() => desfazerRecebimento(cr)}>
                                <RotateCcw size={14} />
                              </button>
                            )}
                            <button style={s.btnDel} title="Excluir" onClick={() => excluirConta(cr.id)}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {emRecebimento && (
                        <tr>
                          <td colSpan={7} style={{ background: 'rgba(16,185,129,0.08)', padding: '0.75rem 1rem', borderBottom: '1px solid #e5e0ef' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, color: '#10b981', fontWeight: 600 }}>Data de recebimento:</span>
                              <input type="date" style={{ ...s.input, maxWidth: 160 }} value={dataRecebimento} onChange={e => setDataRecebimento(e.target.value)} />
                              <button style={{ ...s.btnSalvar, background: '#10b981', padding: '7px 16px' }} onClick={() => registrarRecebimento(cr)}>
                                Confirmar Recebimento
                              </button>
                              <button style={s.btnCancelar} onClick={() => setRecebendoId(null)}>Cancelar</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          ABA: FLUXO DE CAIXA
      ════════════════════════════════════════════════════════════════════ */}
      {aba === 'fluxo' && (() => {
        const hojeProj = hoje();
        const CATEGORIAS_CP = ['Aluguel', 'Energia', 'Internet/Telefone', 'Agua', 'Impostos', 'DAS', 'INSS', 'Pro-labore', 'Software/Assinatura', 'Material', 'Contabilidade', 'Seguro', 'Combustivel', 'Manutencao', 'Outros'];
        const CP_VAZIO = { descricao: '', valor: '', vencimento: '', categoria: '', recorrencia: 'unica', observacoes: '' };

        const salvarCP = () => {
          if (!formCP) return;
          if (!formCP.descricao || !formCP.valor || !formCP.vencimento) { toast.aviso('Preencha descricao, valor e vencimento.'); return; }
          if (editandoCpId) {
            setContasPagar(prev => prev.map(cp => cp.id === editandoCpId ? { ...cp, ...formCP } : cp));
            setEditandoCpId(null);
          } else {
            const novaCP = { ...formCP, id: Date.now(), status: 'pendente', criadoEm: new Date().toISOString() };
            setContasPagar(prev => [...prev, novaCP]);
            if (formCP.recorrencia && formCP.recorrencia !== 'unica') {
              const mesesAdd = formCP.recorrencia === 'mensal' ? 1 : formCP.recorrencia === 'trimestral' ? 3 : 12;
              const futuras = [];
              let dataBase = new Date(formCP.vencimento + 'T12:00:00');
              for (let i = 1; i <= (formCP.recorrencia === 'mensal' ? 11 : formCP.recorrencia === 'trimestral' ? 3 : 0); i++) {
                dataBase = new Date(dataBase);
                dataBase.setMonth(dataBase.getMonth() + mesesAdd);
                futuras.push({ ...formCP, id: Date.now() + i, status: 'pendente', vencimento: dataBase.toISOString().slice(0, 10), criadoEm: new Date().toISOString(), recorrenciaOrigem: novaCP.id });
              }
              if (futuras.length > 0) setContasPagar(prev => [...prev, ...futuras]);
            }
          }
          setFormCP(null);
        };
        const togglePago = (id) => setContasPagar(prev => prev.map(cp => cp.id === id ? { ...cp, status: cp.status === 'pago' ? 'pendente' : 'pago', pagoEm: cp.status === 'pago' ? null : new Date().toISOString() } : cp));
        const excluirCP = (id) => { if (window.confirm('Excluir esta conta?')) setContasPagar(prev => prev.filter(cp => cp.id !== id)); };

        // ── EXTRATO: monta todos os lançamentos unificados, ordenados por data ──
        const [fAno, fMes] = filtroMes.split('-');
        // Fonte 1: fluxoCaixa do mês (SEM filtro de tipo, para saldo correto)
        const fcDoMes = fluxoCaixa.filter(f => (f.data || '').startsWith(filtroMes) && f.status !== 'rejeitado');
        const lancFC = fcDoMes.map(fc => ({
          id: fc.id, data: fc.data, descricao: fc.descricao + (fc.clienteNome ? ` — ${fc.clienteNome}` : ''),
          valor: parseFloat(fc.valor || 0), tipo: (fc.tipo || '').toUpperCase(), origem: 'extrato', editavel: fc.origem !== 'extrato',
          contaContabil: fc.contaContabil, raw: fc,
        }));
        // Fonte 2: contas a pagar do mês
        const lancCP = contasPagar
          .filter(cp => { const [a, m] = (cp.vencimento || '').split('-'); return a === fAno && m === fMes; })
          .map(cp => ({
            id: 'cp_' + cp.id, data: cp.vencimento, descricao: cp.descricao + (cp.categoria ? ` (${cp.categoria})` : ''),
            valor: parseFloat(cp.valor || 0), tipo: 'DESPESA', origem: 'conta_pagar',
            status: cp.status, cpId: cp.id, editavel: true,
          }));
        // Fonte 3: contas a receber do mês
        const lancCR = contasReceber
          .filter(cr => { const [a, m] = (cr.dataVencimento || '').split('-'); return a === fAno && m === fMes; })
          .map(cr => ({
            id: 'cr_' + cr.id, data: cr.dataVencimento, descricao: cr.descricao || 'Recebimento',
            valor: parseFloat(cr.valor || 0), tipo: 'RECEITA', origem: 'conta_receber', status: cr.status,
          }));

        // Unifica e ordena por data ASC
        const todosLanc = [...lancFC, ...lancCP, ...lancCR].sort((a, b) => (a.data || '').localeCompare(b.data || ''));

        // Aplica filtro visual (não afeta saldo)
        const lancVisiveis = filtroTipo ? todosLanc.filter(l => l.tipo === filtroTipo) : todosLanc;

        // Agrupa TODOS por dia para calcular saldo correto
        const diasMapCompleto = {};
        todosLanc.forEach(l => {
          const d = l.data || 'sem-data';
          if (!diasMapCompleto[d]) diasMapCompleto[d] = { entradas: 0, saidas: 0 };
          if (l.tipo === 'RECEITA') diasMapCompleto[d].entradas += l.valor;
          else diasMapCompleto[d].saidas += l.valor;
        });

        // Agrupa visíveis por dia para exibição
        const diasMapVisivel = {};
        lancVisiveis.forEach(l => {
          const d = l.data || 'sem-data';
          if (!diasMapVisivel[d]) diasMapVisivel[d] = [];
          diasMapVisivel[d].push(l);
        });

        // Calcula saldo acumulado usando TODOS os lançamentos
        const todasDatas = [...new Set([...Object.keys(diasMapCompleto), ...Object.keys(diasMapVisivel)])].sort();
        let saldoAcum = saldoInicial;
        const saldoPorDia = {};
        todasDatas.forEach(data => {
          const dc = diasMapCompleto[data] || { entradas: 0, saidas: 0 };
          saldoAcum = saldoAcum + dc.entradas - dc.saidas;
          saldoPorDia[data] = saldoAcum;
        });

        // Monta dias visíveis com saldo correto
        const diasVisiveis = Object.keys(diasMapVisivel).sort();
        const diasComSaldo = diasVisiveis.map(data => {
          const lancs = diasMapVisivel[data];
          const dc = diasMapCompleto[data] || { entradas: 0, saidas: 0 };
          return { data, lancs, entradas: dc.entradas, saidas: dc.saidas, saldo: saldoPorDia[data] };
        });
        const saldoMinimo = todasDatas.length > 0 ? Math.min(...todasDatas.map(d => saldoPorDia[d])) : saldoInicial;
        const saldoFinal = todasDatas.length > 0 ? saldoPorDia[todasDatas[todasDatas.length - 1]] : saldoInicial;

        // Totais do mês (sempre de TODOS, não filtrados)
        const totalEntradas = todosLanc.filter(l => l.tipo === 'RECEITA').reduce((ac, l) => ac + l.valor, 0);
        const totalSaidas = todosLanc.filter(l => l.tipo !== 'RECEITA').reduce((ac, l) => ac + l.valor, 0);

        return (
          <>
            {/* Header com saldo + botões */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: 10, color: '#5f5874', fontWeight: 700, textTransform: 'uppercase', display: 'block' }}>SALDO INICIAL</label>
                  {editandoSaldo ? (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                      <input type="number" step="0.01" style={{ ...s.input, width: 140, padding: '6px 10px' }} value={saldoTemp} onChange={e => setSaldoTemp(e.target.value)} autoFocus onKeyDown={e => { if (e.key === 'Enter') { setSaldoInicial(parseFloat(saldoTemp) || 0); localStorage.setItem('kzerodiary_saldo_inicial', saldoTemp); setEditandoSaldo(false); }}} />
                      <button onClick={() => { setSaldoInicial(parseFloat(saldoTemp) || 0); localStorage.setItem('kzerodiary_saldo_inicial', saldoTemp); setEditandoSaldo(false); }} style={{ background: '#10b981', border: 'none', borderRadius: 6, color: 'white', padding: '4px 10px', cursor: 'pointer' }}><CheckCircle size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setSaldoTemp(String(saldoInicial)); setEditandoSaldo(true); }} style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, color: '#10b981', padding: '6px 14px', fontSize: 18, fontWeight: 700, fontFamily: 'monospace', cursor: 'pointer', marginTop: 4 }}>
                      {fmtMoeda(saldoInicial)} <Pencil size={12} style={{ marginLeft: 6, opacity: 0.6 }} />
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button onClick={() => setForm({ tipo: 'RECEITA', categoria: categorias[0] || '', descricao: '', valor: '', data: hoje() })} style={{ ...s.btnNovo, background: '#10b981', marginLeft: 0 }}><Plus size={15} /> Entrada</button>
                <button onClick={() => setForm({ tipo: 'DESPESA', categoria: categorias[0] || '', descricao: '', valor: '', data: hoje() })} style={{ ...s.btnNovo, background: '#ef4444', marginLeft: 0 }}><Plus size={15} /> Saida</button>
                <button onClick={() => { setFormCP(CP_VAZIO); setEditandoCpId(null); }} style={{ ...s.btnNovo, marginLeft: 0 }}><Plus size={15} /> Conta a Pagar</button>
              </div>
            </div>

            {/* KPIs */}
            <div className="mobile-grid-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px,1fr))', gap: '0.75rem' }}>
              <div style={s.resumoCard}><div style={s.resumoLabel}>Entradas</div><div style={{ ...s.resumoValor, color: '#10b981' }}>{fmtMoeda(totalEntradas)}</div></div>
              <div style={s.resumoCard}><div style={s.resumoLabel}>Saidas</div><div style={{ ...s.resumoValor, color: '#ef4444' }}>{fmtMoeda(totalSaidas)}</div></div>
              <div style={s.resumoCard}><div style={s.resumoLabel}>Resultado</div><div style={{ ...s.resumoValor, color: totalEntradas - totalSaidas >= 0 ? '#10b981' : '#ef4444' }}>{fmtMoeda(totalEntradas - totalSaidas)}</div></div>
              <div style={{ ...s.resumoCard, borderColor: saldoMinimo < 0 ? 'rgba(239,68,68,0.4)' : '#e5e0ef' }}><div style={s.resumoLabel}>Saldo Final</div><div style={{ ...s.resumoValor, color: saldoFinal >= 0 ? '#10b981' : '#ef4444' }}>{fmtMoeda(saldoFinal)}</div></div>
            </div>

            {/* Alerta saldo negativo */}
            {saldoMinimo < 0 && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
                <AlertCircle size={20} color="#ef4444" />
                <div style={{ fontSize: 13, color: '#ef4444' }}>
                  <strong>Saldo negativo detectado!</strong> Voce precisa de mais <strong>{fmtMoeda(Math.abs(saldoMinimo))}</strong> para cobrir todos os vencimentos do mes.
                </div>
              </div>
            )}

            {/* Filtro de mês */}
            <div style={s.filtrosCard}>
              <input type="month" style={s.select} value={filtroMes} onChange={e => setFiltroMes(e.target.value)} />
              <select style={s.select} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                <option value="">Todos</option><option value="RECEITA">Entradas</option><option value="DESPESA">Saidas</option>
              </select>
            </div>

            {/* Form novo lançamento */}
            {form && (
              <div ref={formRef} style={{ ...s.formCard, border: editandoId ? '1px solid #7c3aed' : '1px solid #e5e0ef' }}>
                <h3 style={s.formTitulo}>{editandoId ? 'Editar' : form.tipo === 'RECEITA' ? 'Nova Entrada' : 'Nova Saida'}</h3>
                <div style={s.formRow}>
                  <div style={s.campo}><label style={s.label}>Data</label><input type="date" style={s.input} value={form.data} onChange={e => setForm({ ...form, data: e.target.value })} /></div>
                  <div style={s.campo}><label style={s.label}>Tipo</label><select style={s.input} value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}><option value="RECEITA">Entrada</option><option value="DESPESA">Saida</option></select></div>
                  <div style={s.campo}><label style={s.label}>Categoria</label><select style={s.input} value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })}>{categorias.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                </div>
                <div style={s.formRow}>
                  <div style={{ ...s.campo, flex: 2 }}><label style={s.label}>Descricao *</label><input type="text" style={s.input} value={form.descricao} placeholder="Descreva" onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
                  <div style={s.campo}><label style={s.label}>Valor *</label><input type="number" style={s.input} value={form.valor} step="0.01" placeholder="0,00" onChange={e => setForm({ ...form, valor: e.target.value })} /></div>
                </div>
                <div style={s.formActions}><button style={s.btnSalvar} onClick={salvarLancamento}>Salvar</button><button style={s.btnCancelar} onClick={() => { setForm(null); setEditandoId(null); }}>Cancelar</button></div>
              </div>
            )}

            {/* Form conta a pagar */}
            {formCP && (
              <div style={{ ...s.formCard, border: '1px solid rgba(124,58,237,0.3)' }}>
                <h3 style={s.formTitulo}>{editandoCpId ? 'Editar Conta a Pagar' : 'Nova Conta a Pagar'}</h3>
                <div style={s.formRow}>
                  <div style={s.campo}><label style={s.label}>Descricao *</label><input style={s.input} value={formCP.descricao} onChange={e => setFormCP({...formCP, descricao: e.target.value})} placeholder="Ex: Aluguel" /></div>
                  <div style={s.campo}><label style={s.label}>Valor *</label><input type="number" step="0.01" style={s.input} value={formCP.valor} onChange={e => setFormCP({...formCP, valor: e.target.value})} /></div>
                  <div style={s.campo}><label style={s.label}>Vencimento *</label><input type="date" style={s.input} value={formCP.vencimento} onChange={e => setFormCP({...formCP, vencimento: e.target.value})} /></div>
                  <div style={s.campo}><label style={s.label}>Categoria</label><select style={s.input} value={formCP.categoria} onChange={e => setFormCP({...formCP, categoria: e.target.value})}><option value="">—</option>{CATEGORIAS_CP.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                  <div style={s.campo}><label style={s.label}>Recorrencia</label><select style={s.input} value={formCP.recorrencia} onChange={e => setFormCP({...formCP, recorrencia: e.target.value})}><option value="unica">Unica</option><option value="mensal">Mensal</option><option value="trimestral">Trimestral</option><option value="anual">Anual</option></select></div>
                </div>
                <div style={s.formActions}><button style={s.btnSalvar} onClick={salvarCP}>{editandoCpId ? 'Salvar' : 'Adicionar'}</button><button style={s.btnCancelar} onClick={() => { setFormCP(null); setEditandoCpId(null); }}>Cancelar</button></div>
              </div>
            )}

            {/* ── EXTRATO BANCÁRIO ── */}
            <div style={s.tabelaCard}>
              {diasComSaldo.length === 0 ? (
                <div style={s.tdVazio}>Nenhum lancamento no periodo. Importe um extrato CSV ou adicione entradas/saidas.</div>
              ) : diasComSaldo.map((dia, di) => {
                const dObj = dia.data !== 'sem-data' ? new Date(dia.data + 'T12:00:00') : null;
                const negativo = dia.saldo < 0;
                return (
                  <div key={di}>
                    {/* Cabeçalho do dia */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', padding: '10px 16px', background: '#f7f5fb', borderBottom: '1px solid #eeeaf5', borderTop: di > 0 ? '2px solid #e5e0ef' : 'none' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1c1530', minWidth: 0 }}>
                        {dObj ? dObj.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Sem data'}
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        {dia.entradas > 0 && <span style={{ fontSize: 12, color: '#10b981', fontFamily: 'monospace' }}>+ {fmtMoeda(dia.entradas)}</span>}
                        {dia.saidas > 0 && <span style={{ fontSize: 12, color: '#ef4444', fontFamily: 'monospace' }}>- {fmtMoeda(dia.saidas)}</span>}
                        <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: negativo ? '#ef4444' : '#10b981', background: negativo ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)', padding: '4px 12px', borderRadius: 6 }}>
                          {fmtMoeda(dia.saldo)}
                        </span>
                      </div>
                    </div>
                    {/* Lançamentos do dia */}
                    {dia.lancs.map(l => {
                      const isEntrada = l.tipo === 'RECEITA';
                      const isPago = l.status === 'pago' || l.status === 'recebido';
                      const isCP = l.origem === 'conta_pagar';
                      const emEdicao = editandoId === l.id;
                      return (
                        <div key={l.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid #eeeaf5', background: emEdicao ? 'rgba(124,58,237,0.06)' : isPago ? 'rgba(139,161,158,0.03)' : 'transparent', gap: '0.75rem' }}>
                          {isCP && <input type="checkbox" checked={isPago} onChange={() => togglePago(l.cpId)} style={{ cursor: 'pointer', accentColor: '#10b981', flexShrink: 0 }} />}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, color: isPago ? '#8a849c' : '#1c1530', textDecoration: isPago ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {l.descricao}
                            </div>
                            <div style={{ fontSize: 11, color: '#8a849c', display: 'flex', gap: 6, marginTop: 2, flexWrap: 'wrap', minWidth: 0 }}>
                              {l.origem === 'extrato' && <span style={{ background: 'rgba(88,166,255,0.1)', color: '#1d4ed8', padding: '0 6px', borderRadius: 8 }}>extrato</span>}
                              {l.origem === 'conta_pagar' && <span style={{ background: 'rgba(124,58,237,0.1)', color: '#6d28d9', padding: '0 6px', borderRadius: 8 }}>conta a pagar</span>}
                              {l.origem === 'conta_receber' && <span style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', padding: '0 6px', borderRadius: 8 }}>a receber</span>}
                              {l.contaContabil && <span style={{ color: '#8a849c' }}>{l.contaContabil}</span>}
                              {isCP && !isPago && l.status !== 'pago' && <span style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', padding: '0 6px', borderRadius: 8, fontWeight: 600 }}>pendente</span>}
                              {l.raw?.anexos?.length > 0 && (
                                <span
                                  onClick={() => setAnexosAbertoId(l.raw.id)}
                                  style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', padding: '0 6px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                  title={`${l.raw.anexos.length} anexo(s) PDF`}
                                >
                                  <Paperclip size={10} /> {l.raw.anexos.length}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'monospace', color: isPago ? '#8a849c' : isEntrada ? '#10b981' : '#ef4444', textDecoration: isPago ? 'line-through' : 'none', whiteSpace: 'nowrap' }}>
                            {isEntrada ? '+' : '-'} {fmtMoeda(l.valor)}
                          </div>
                          <div style={{ ...s.acoes, flexShrink: 0 }}>
                            {l.raw && <button style={s.btnEdit} title="Anexar PDF" onClick={() => setAnexosAbertoId(l.raw.id)}><Paperclip size={13} /></button>}
                            {l.raw && <button style={s.btnEdit} title="Editar" onClick={() => editarLancamento(l.raw)}><Pencil size={13} /></button>}
                            {isCP && <button style={s.btnEdit} title="Editar" onClick={() => { const cp = contasPagar.find(c => c.id === l.cpId); if (cp) { setFormCP({ descricao: cp.descricao, valor: cp.valor, vencimento: cp.vencimento, categoria: cp.categoria || '', recorrencia: cp.recorrencia || 'unica', observacoes: cp.observacoes || '' }); setEditandoCpId(cp.id); }}}><Pencil size={13} /></button>}
                            {isCP && <button style={s.btnDel} title="Excluir" onClick={() => excluirCP(l.cpId)}><Trash2 size={13} /></button>}
                            {l.raw && l.editavel && <button style={s.btnDel} title="Excluir" onClick={() => deletarLancamento(l.id)}><Trash2 size={13} /></button>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════
          ABA: DIVISÃO DE LUCROS
      ════════════════════════════════════════════════════════════════════ */}
      {aba === 'divisao' && (() => {
        const { receitas, acumuladosAReceber, reembolsosAReceber, receitaTotal, despesas, despesasLista, lucroLiquido, lucroDisponivel, calculadas, totalDistribuido, saldoRestante, excedente, isEstimativa } = divResultado;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={s.secaoCard}>
              <div style={s.secaoHeader}><PieChart size={20} color="#8b5cf6" /><div><div style={s.secaoTitulo}>Divisão de Lucros</div><div style={s.secaoDesc}>Selecione o <strong>mês de competência</strong> das receitas e o <strong>mês das despesas</strong> a considerar no cálculo.</div></div></div>
              <div className="mobile-grid-1" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={s.campo}>
                  <label style={s.label}>Mês de Competência (receitas)</label>
                  <input type="month" style={s.input} value={divMesCompetencia} onChange={e => setDivMesCompetencia(e.target.value)} />
                </div>
                <div style={s.campo}>
                  <label style={s.label}>Mês das Despesas</label>
                  <input type="month" style={s.input} value={divMesDespesas} onChange={e => setDivMesDespesas(e.target.value)} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: usarAgendaEstimativa ? '#6d28d9' : '#5f5874', background: 'rgba(139,92,246,0.06)', border: `1px solid ${usarAgendaEstimativa ? '#7c3aed' : '#e5e0ef'}`, borderRadius: 8, padding: '6px 12px', userSelect: 'none', alignSelf: 'flex-end' }}>
                  <input type="checkbox" checked={usarAgendaEstimativa} onChange={e => setUsarAgendaEstimativa(e.target.checked)} style={{ accentColor: '#8b5cf6' }} />
                  Usar agenda como estimativa de receitas
                </label>
              </div>
            </div>

            <div className="mobile-grid-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '0.75rem' }}>
              {[
                { label: `Serviços (ref. ${fmtMes(divMesCompetencia)})`, valor: receitas, cor: '#10b981', estimativa: isEstimativa },
                ...(acumuladosAReceber > 0 ? [{ label: `Acumulados a Receber (ref. ${fmtMes(divMesCompetencia)})`, valor: acumuladosAReceber, cor: '#f59e0b' }] : []),
                ...(reembolsosAReceber > 0 ? [{ label: 'Reembolsos a Receber (não entra na divisão)', valor: reembolsosAReceber, cor: '#6b7280' }] : []),
                { label: `Despesas (${fmtMes(divMesDespesas)})`, valor: despesas, cor: '#ef4444', clicavel: true },
                { label: 'Lucro Líquido', valor: lucroLiquido, cor: lucroLiquido >= 0 ? '#a78bfa' : '#ef4444' },
                { label: 'Lucro Disponível p/ Distribuir', valor: lucroDisponivel, cor: lucroDisponivel >= 0 ? '#10b981' : '#ef4444', destaque: true },
              ].map(({ label, valor, cor, estimativa, destaque, clicavel }) => (
                <div key={label} onClick={clicavel ? () => setMostrarDespDiv(v => !v) : undefined} style={{ ...s.resumoCard, border: destaque ? '1px solid #8b5cf6' : '1px solid #e5e0ef', cursor: clicavel ? 'pointer' : 'default' }}>
                  <div style={s.resumoLabel}>{label}{estimativa && <span style={{ marginLeft: 6, fontSize: 10, background: 'rgba(251,191,36,0.15)', color: '#b45309', borderRadius: 6, padding: '1px 6px', fontWeight: 600 }}>Estimado (agenda)</span>}{clicavel && <span style={{ marginLeft: 6, fontSize: 10, color: '#5f5874' }}>(clique p/ detalhar)</span>}</div>
                  <div style={{ ...s.resumoValor, fontSize: '1.4rem', color: cor }}>{fmtMoeda(valor)}</div>
                </div>
              ))}
            </div>

            {/* Detalhamento das despesas */}
            {mostrarDespDiv && (
              <div style={{ ...s.secaoCard, border: '1px solid rgba(239,68,68,0.3)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1c1530' }}>Despesas incluidas no calculo — {fmtMes(divMesDespesas)}</div>
                  <button onClick={() => setMostrarDespDiv(false)} style={{ background: 'transparent', border: 'none', color: '#5f5874', cursor: 'pointer', fontSize: 18 }}>×</button>
                </div>
                <div style={{ fontSize: 11, color: '#5f5874', marginBottom: '0.5rem' }}>Exclui lancamentos com categoria "Antecipacao" e lancamentos rejeitados.</div>
                {despesasLista.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '1rem', color: '#8a849c', fontSize: 13 }}>Nenhuma despesa no periodo.</div>
                ) : (
                  <table style={s.tabela}>
                    <thead><tr><th style={s.th}>Data</th><th style={s.th}>Descricao</th><th style={s.th}>Categoria</th><th style={{ ...s.th, textAlign: 'right' }}>Valor</th></tr></thead>
                    <tbody>
                      {despesasLista.sort((a, b) => (a.data || '').localeCompare(b.data || '')).map(f => (
                        <tr key={f.id}>
                          <td style={s.td}>{f.data ? new Date(f.data + 'T00:00').toLocaleDateString('pt-BR') : '—'}</td>
                          <td style={s.td}>{f.descricao}{f.clienteNome ? ` — ${f.clienteNome}` : ''}</td>
                          <td style={s.td}><span style={{ fontSize: 12, color: '#5f5874' }}>{f.categoria || '—'}</span></td>
                          <td style={{ ...s.td, textAlign: 'right', fontWeight: 600, color: '#ef4444', fontFamily: 'monospace' }}>{fmtMoeda(f.valor)}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid #e5e0ef' }}>
                        <td colSpan="3" style={{ ...s.td, fontWeight: 700, color: '#1c1530' }}>Total</td>
                        <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: '#ef4444', fontFamily: 'monospace', fontSize: 15 }}>{fmtMoeda(despesas)}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            )}

            <div style={s.secaoCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div style={s.secaoTitulo}>Participantes na Divisão</div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {!formQuota && <button style={s.btnNovo} onClick={() => setFormQuota({ nome: '', tipo: 'percentual', valor: '' })}><Plus size={14} /> Adicionar</button>}
                  <button style={{ ...s.btnAcao, background: '#7c3aed', padding: '7px 14px', fontSize: 12 }} onClick={exportarDivisaoPDF}>PDF</button>
                  <button style={{ ...s.btnAcao, background: '#1d4ed8', padding: '7px 14px', fontSize: 12 }} onClick={exportarDivisaoWord}>Word</button>
                </div>
              </div>

              {formQuota && (
                <div style={{ background: '#ffffff', borderRadius: 10, padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <div style={s.formRow}>
                    <div style={{ ...s.campo, flex: 2 }}>
                      <label style={s.label}>Nome / Função *</label>
                      <input type="text" style={s.input} placeholder="Ex: Bianca – Sócia" value={formQuota.nome} onChange={e => setFormQuota({ ...formQuota, nome: e.target.value })} />
                    </div>
                    <div style={s.campo}>
                      <label style={s.label}>Tipo</label>
                      <select style={s.input} value={formQuota.tipo} onChange={e => setFormQuota({ ...formQuota, tipo: e.target.value })}>
                        <option value="percentual">Percentual (%)</option>
                        <option value="fixo">Valor Fixo (R$)</option>
                      </select>
                    </div>
                    <div style={s.campo}>
                      <label style={s.label}>{formQuota.tipo === 'percentual' ? 'Percentual (%)' : 'Valor (R$)'}</label>
                      <input type="number" style={s.input} step="0.01" placeholder={formQuota.tipo === 'percentual' ? '0-100' : '0,00'} value={formQuota.valor} onChange={e => setFormQuota({ ...formQuota, valor: e.target.value })} />
                    </div>
                  </div>
                  <div style={s.formActions}>
                    <button style={s.btnSalvar} onClick={salvarQuota}>Salvar</button>
                    <button style={s.btnCancelar} onClick={() => setFormQuota(null)}>Cancelar</button>
                  </div>
                </div>
              )}

              {quotas.length === 0 && !formQuota && (
                <div style={{ color: '#64748b', fontSize: 13, textAlign: 'center', padding: '1rem' }}>Nenhum participante cadastrado. Adicione sócios ou consultores.</div>
              )}

              {calculadas.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {calculadas.map(q => {
                    const aberto = expandidoId === q.id;
                    const temFaltante = q.faltante !== 0 && q.valorPago !== null;
                    return (
                      <div key={q.id} style={{ border: `1px solid ${aberto ? '#7c3aed' : '#e5e0ef'}`, borderRadius: 12, overflow: 'hidden' }}>
                        {/* Linha resumo */}
                        <div className="linha-participante" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr auto', gap: '0.5rem', alignItems: 'center', padding: '0.75rem 1rem', background: aberto ? 'rgba(124,58,237,0.06)' : 'transparent', cursor: 'pointer' }} onClick={() => setExpandidoId(aberto ? null : q.id)}>
                          <div>
                            <div style={{ fontWeight: 700, color: '#1c1530' }}>{q.nome}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{q.tipo === 'percentual' ? `${q.valor}% do lucro` : 'Valor fixo'}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 10, color: '#64748b' }}>Base</div>
                            <div style={{ color: '#64748b', fontWeight: 600 }}>{fmtMoeda(q.base)}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 10, color: '#64748b' }}>Acertos</div>
                            <div style={{ color: q.totalAcertos >= 0 ? '#047857' : '#dc2626', fontWeight: 600 }}>{q.totalAcertos >= 0 ? '+' : ''}{fmtMoeda(q.totalAcertos)}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 10, color: '#64748b' }}>Com Acertos</div>
                            <div style={{ color: '#64748b', fontWeight: 600 }}>{fmtMoeda(q.valorComAcertos)}</div>
                          </div>
                          {(q.totalGastosPessoais > 0 || q.totalReceitasPessoais > 0) && (
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: 10, color: '#64748b' }}>Ajustes Pessoais</div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                {q.totalGastosPessoais > 0 && <span style={{ color: '#dc2626', fontWeight: 600 }}>−{fmtMoeda(q.totalGastosPessoais)}</span>}
                                {q.totalReceitasPessoais > 0 && <span style={{ color: '#047857', fontWeight: 600 }}>+{fmtMoeda(q.totalReceitasPessoais)}</span>}
                              </div>
                            </div>
                          )}
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 10, color: '#64748b' }}>A Receber</div>
                            <div style={{ color: '#10b981', fontWeight: 700, fontSize: 15 }}>{fmtMoeda(q.valorLiquido)}</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 10, color: '#64748b' }}>Pago / Faltante</div>
                            {q.valorPago !== null
                              ? <div><span style={{ color: '#1d4ed8' }}>{fmtMoeda(q.valorPago)}</span>{temFaltante && <span style={{ marginLeft: 4, fontSize: 11, color: q.faltante > 0 ? '#dc2626' : '#047857' }}>{q.faltante > 0 ? `−${fmtMoeda(q.faltante)}` : `+${fmtMoeda(-q.faltante)}`}</span>}</div>
                              : <div style={{ color: '#475569', fontSize: 12 }}>não informado</div>}
                          </div>
                          <div style={{ display: 'flex', gap: 4, paddingLeft: 8 }} onClick={e => e.stopPropagation()}>
                            <button style={s.btnEdit} title="Editar participante" onClick={() => setFormQuota({ ...q })}><Pencil size={13} /></button>
                            <button style={s.btnDel} title="Remover" onClick={() => { setQuotas(quotas.filter(x => x.id !== q.id)); toast.sucesso('Participante removido.'); }}><Trash2 size={13} /></button>
                          </div>
                        </div>

                        {/* Painel expandido de acertos */}
                        {aberto && (
                          <div style={{ borderTop: '1px solid #e5e0ef', padding: '1rem', background: '#ffffff', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>Acertos de {fmtMes(divMesCompetencia)}</div>
                              <button style={{ ...s.btnNovo, padding: '5px 12px', fontSize: 12 }} onClick={() => setFormAcerto({ quotaId: q.id, descricao: '', valor: '' })}><Plus size={13} /> Adicionar acerto</button>
                            </div>

                            {/* Form novo acerto */}
                            {formAcerto?.quotaId === q.id && (
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', background: '#f3f0f9', borderRadius: 8, padding: '0.75rem' }}>
                                <div style={{ flex: 2 }}>
                                  <div style={{ ...s.label, marginBottom: 4 }}>Descrição</div>
                                  <input style={s.input} placeholder="Ex: Microsoft, adiantamento, Uber..." value={formAcerto.descricao} onChange={e => setFormAcerto({ ...formAcerto, descricao: e.target.value })} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ ...s.label, marginBottom: 4 }}>Valor (negativo = desconto)</div>
                                  <input type="number" step="0.01" style={s.input} placeholder="Ex: 60 ou -300" value={formAcerto.valor} onChange={e => setFormAcerto({ ...formAcerto, valor: e.target.value })} />
                                </div>
                                <button style={s.btnSalvar} onClick={salvarAcerto}>OK</button>
                                <button style={s.btnCancelar} onClick={() => setFormAcerto(null)}>✕</button>
                              </div>
                            )}

                            {/* Lista de acertos */}
                            {q.acertos.length === 0 && !formAcerto
                              ? <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '0.5rem' }}>Nenhum acerto para {fmtMes(divMesCompetencia)}. Use para registrar despesas pagas, ressarcimentos ou antecipações (valor negativo).</div>
                              : q.acertos.map(a => (
                                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.75rem', background: '#f3f0f9', borderRadius: 8 }}>
                                  <span style={{ fontSize: 13, color: '#475569' }}>{a.descricao}</span>
                                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <span style={{ fontWeight: 700, color: parseFloat(a.valor) >= 0 ? '#047857' : '#dc2626' }}>{parseFloat(a.valor) >= 0 ? '+' : ''}{fmtMoeda(a.valor)}</span>
                                    <button style={s.btnEdit} onClick={() => setFormAcerto({ ...a, quotaId: q.id })}><Pencil size={12} /></button>
                                    <button style={s.btnDel} onClick={() => excluirAcerto(q.id, a.id)}><Trash2 size={12} /></button>
                                  </div>
                                </div>
                              ))
                            }

                            {/* Ajustes Pessoais (gastos e valores a receber) */}
                            <div style={{ borderTop: '1px solid #e5e0ef', paddingTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>Ajustes Pessoais (no repasse)</div>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button style={{ ...s.btnNovo, padding: '5px 12px', fontSize: 12, background: 'rgba(248,113,113,0.12)', color: '#dc2626', borderColor: '#f87171' }} onClick={() => setFormGasto({ quotaId: q.id, descricao: '', valor: '', tipo: 'gasto' })}><Plus size={13} /> Gasto (desconto)</button>
                                  <button style={{ ...s.btnNovo, padding: '5px 12px', fontSize: 12, background: 'rgba(52,211,153,0.12)', color: '#047857', borderColor: '#34d399' }} onClick={() => setFormGasto({ quotaId: q.id, descricao: '', valor: '', tipo: 'receita' })}><Plus size={13} /> A Receber (acréscimo)</button>
                                </div>
                              </div>
                              {formGasto?.quotaId === q.id && (
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', background: '#f3f0f9', borderRadius: 8, padding: '0.75rem', flexWrap: 'wrap' }}>
                                  <div style={{ flex: '0 0 130px' }}>
                                    <div style={{ ...s.label, marginBottom: 4 }}>Tipo</div>
                                    <select style={s.input} value={formGasto.tipo || 'gasto'} onChange={e => setFormGasto({ ...formGasto, tipo: e.target.value })}>
                                      <option value="gasto">Gasto (−)</option>
                                      <option value="receita">A Receber (+)</option>
                                    </select>
                                  </div>
                                  <div style={{ flex: 2 }}>
                                    <div style={{ ...s.label, marginBottom: 4 }}>Descrição</div>
                                    <input style={s.input} placeholder={formGasto.tipo === 'receita' ? 'Ex: reembolso pendente, bônus...' : 'Ex: almoço cliente, assinatura pessoal...'} value={formGasto.descricao} onChange={e => setFormGasto({ ...formGasto, descricao: e.target.value })} />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ ...s.label, marginBottom: 4 }}>Valor (positivo)</div>
                                    <input type="number" step="0.01" min="0" style={s.input} placeholder="Ex: 150" value={formGasto.valor} onChange={e => setFormGasto({ ...formGasto, valor: e.target.value })} />
                                  </div>
                                  <button style={s.btnSalvar} onClick={salvarGasto}>OK</button>
                                  <button style={s.btnCancelar} onClick={() => setFormGasto(null)}>✕</button>
                                </div>
                              )}
                              {q.gastosPessoais.length === 0 && !formGasto
                                ? <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '0.3rem' }}>Nenhum ajuste lançado. Use "Gasto" para descontar do repasse ou "A Receber" para acrescentar, sem afetar a divisão de lucros das demais sócias.</div>
                                : q.gastosPessoais.map(g => {
                                  const isReceita = g.tipo === 'receita';
                                  const bg = isReceita ? 'rgba(52,211,153,0.07)' : 'rgba(248,113,113,0.07)';
                                  const border = isReceita ? '1px solid rgba(52,211,153,0.2)' : '1px solid rgba(248,113,113,0.2)';
                                  const corTexto = isReceita ? '#6ee7b7' : '#fca5a5';
                                  const corValor = isReceita ? '#34d399' : '#f87171';
                                  const sinal = isReceita ? '+' : '−';
                                  return (
                                    <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0.75rem', background: bg, border, borderRadius: 8 }}>
                                      <span style={{ fontSize: 13, color: corTexto }}>{g.descricao}{isReceita && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(52,211,153,0.2)', color: '#047857', textTransform: 'uppercase', letterSpacing: 0.5 }}>a receber</span>}</span>
                                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <span style={{ fontWeight: 700, color: corValor }}>{sinal}{fmtMoeda(Math.abs(g.valor))}</span>
                                        <button style={s.btnEdit} onClick={() => setFormGasto({ ...g, quotaId: q.id, tipo: g.tipo || 'gasto' })}><Pencil size={12} /></button>
                                        <button style={s.btnDel} onClick={() => excluirGasto(q.id, g.id)}><Trash2 size={12} /></button>
                                      </div>
                                    </div>
                                  );
                                })
                              }
                              {q.totalGastosPessoais > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#dc2626', fontWeight: 700, padding: '0.2rem 0.75rem' }}>
                                  <span>Total gastos pessoais:</span>
                                  <span>−{fmtMoeda(q.totalGastosPessoais)}</span>
                                </div>
                              )}
                              {q.totalReceitasPessoais > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#047857', fontWeight: 700, padding: '0.2rem 0.75rem' }}>
                                  <span>Total a receber:</span>
                                  <span>+{fmtMoeda(q.totalReceitasPessoais)}</span>
                                </div>
                              )}
                            </div>

                            {/* Valor pago */}
                            <div style={{ borderTop: '1px solid #e5e0ef', paddingTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Valor efetivamente pago a {q.nome.split(' ')[0]}:</div>
                              <input type="number" step="0.01" style={{ ...s.input, maxWidth: 160 }} placeholder={fmtMoeda(q.valorLiquido)} value={q.valorPago !== null && q.valorPago !== undefined ? q.valorPago : ''} onChange={e => setValorPago(q.id, e.target.value)} />
                              {q.valorPago !== null && temFaltante && (
                                <div style={{ fontSize: 12, padding: '4px 10px', borderRadius: 8, background: q.faltante > 0 ? 'rgba(248,113,113,0.1)' : 'rgba(52,211,153,0.1)', color: q.faltante > 0 ? '#dc2626' : '#047857', fontWeight: 600 }}>
                                  {q.faltante > 0 ? `Faltante: ${fmtMoeda(q.faltante)}` : `Excesso: ${fmtMoeda(-q.faltante)}`}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Aviso de excedente */}
                  {excedente && (
                    <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid #fbbf24', borderRadius: 8, padding: '0.6rem 1rem', fontSize: 12, color: '#b45309', display: 'flex', alignItems: 'center', gap: 8 }}>
                      ⚠️ A soma das cotas ultrapassaria o lucro disponível. Os valores foram ajustados proporcionalmente para não gerar saldo negativo.
                    </div>
                  )}
                  {/* Totais */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '0.5rem 0' }}>
                    <div style={{ fontSize: 13, color: '#64748b' }}>Total distribuído: <strong style={{ color: '#818cf8' }}>{fmtMoeda(totalDistribuido)}</strong></div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>Saldo restante: <strong style={{ color: '#10b981' }}>{fmtMoeda(saldoRestante)}</strong></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════
          ABA: CATEGORIAS
      ════════════════════════════════════════════════════════════════════ */}
      {aba === 'categorias' && (
        <div style={s.secaoCard}>
          <div style={s.secaoHeader}><Tag size={20} color="#8b5cf6" /><div><div style={s.secaoTitulo}>Gerenciar Categorias</div><div style={s.secaoDesc}>Adicione ou remova categorias de lançamentos financeiros.</div></div></div>
          <div style={s.catForm}>
            <input type="text" style={s.input} value={novaCategoria} placeholder="Nome da nova categoria" onChange={e => setNovaCategoria(e.target.value)} onKeyDown={e => e.key === 'Enter' && adicionarCategoria()} />
            <button style={s.btnNovo} onClick={adicionarCategoria}><Plus size={15} /> Adicionar</button>
          </div>
          <div style={s.catLista}>
            {categorias.map(cat => (
              <div key={cat} style={s.catItem}>
                <span style={s.catNome}>{cat}</span>
                <button style={s.btnDel} onClick={() => setCategorias(categorias.filter(c => c !== cat))}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          ABA: MENSALIDADES (planilha cliente × mês)
      ════════════════════════════════════════════════════════════════════ */}
      {aba === 'mensalidades' && (() => {
        const MESES_LABEL = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
        const STATUS_CYCLE = { pendente: 'pago', pago: 'atrasado', atrasado: 'pendente' };
        const STATUS_COR = {
          pendente: { bg: 'transparent',           borda: '#eeeaf5', icon: '⏳' },
          pago:     { bg: 'rgba(16,185,129,0.10)', borda: '#10b981', icon: '✅' },
          atrasado: { bg: 'rgba(239,68,68,0.10)',  borda: '#ef4444', icon: '❌' },
        };

        const getCelula = (clienteId, mes) =>
          (mensalidades?.[clienteId]?.[anoMensalidade]?.[mes]) || { valor: 0, status: 'pendente' };

        const setCelula = (clienteId, mes, patch) => {
          setMensalidades(prev => {
            const m = prev || {};
            const porCli = m[clienteId] || {};
            const porAno = porCli[anoMensalidade] || {};
            const atual = porAno[mes] || { valor: 0, status: 'pendente' };
            return {
              ...m,
              [clienteId]: {
                ...porCli,
                [anoMensalidade]: { ...porAno, [mes]: { ...atual, ...patch } },
              },
            };
          });
        };

        const totalLinha = (clienteId) => {
          let t = 0;
          for (let m = 1; m <= 12; m++) t += parseFloat(getCelula(clienteId, m).valor || 0);
          return t;
        };

        // Aplica filtro "todas" vs "a_cobrar" (qualquer mês com valor > 0 no ano)
        const clientesVisiveis = filtroMensalidades === 'a_cobrar'
          ? clientes.filter(c => totalLinha(c.id) > 0)
          : clientes;

        const totalColuna = (mes) => {
          let t = 0;
          for (const c of clientesVisiveis) t += parseFloat(getCelula(c.id, mes).valor || 0);
          return t;
        };
        const totalGeral = clientesVisiveis.reduce((acc, c) => acc + totalLinha(c.id), 0);

        const stickyCol = { position: 'sticky', left: 0, zIndex: 1 };
        const tdMes = (status) => ({
          ...s.td,
          padding: '4px 6px',
          background: STATUS_COR[status].bg,
          borderLeft: `1px solid ${STATUS_COR[status].borda === '#eeeaf5' ? '#eeeaf5' : STATUS_COR[status].borda}`,
        });

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={s.filtrosCard}>
              <label style={{ ...s.label, marginRight: 4, marginBottom: 0 }}>Ano</label>
              <select
                style={s.select}
                value={anoMensalidade}
                onChange={e => setAnoMensalidade(parseInt(e.target.value, 10))}
              >
                {[2024, 2025, 2026, 2027, 2028].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <label style={{ ...s.label, marginRight: 4, marginBottom: 0, marginLeft: 12 }}>Mostrar</label>
              <select
                style={s.select}
                value={filtroMensalidades}
                onChange={e => setFiltroMensalidades(e.target.value)}
              >
                <option value="todas">Todas as empresas</option>
                <option value="a_cobrar">Com valores a cobrar</option>
              </select>
              <div style={{ marginLeft: 'auto', fontSize: 13, color: '#5f5874' }}>
                Total {anoMensalidade}:{' '}
                <strong style={{ color: '#10b981', fontSize: 16, marginLeft: 6 }}>{fmtMoeda(totalGeral)}</strong>
              </div>
            </div>

            <div style={{ ...s.tabelaCard, overflowX: 'auto' }}>
              {clientes.length === 0 ? (
                <div style={s.tdVazio}>
                  Nenhum cliente cadastrado. Cadastre clientes em <strong>Pessoas</strong> para começar a registrar mensalidades.
                </div>
              ) : clientesVisiveis.length === 0 ? (
                <div style={s.tdVazio}>
                  Nenhuma empresa com valores a cobrar em {anoMensalidade}. Troque o filtro para <strong>Todas as empresas</strong> para ver toda a base.
                </div>
              ) : (
                <table style={{ ...s.tabela, minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, ...stickyCol, background: '#f7f5fb', minWidth: 180 }}>Cliente</th>
                      {MESES_LABEL.map(label => (
                        <th key={label} style={{ ...s.th, textAlign: 'center', minWidth: 100 }}>{label}</th>
                      ))}
                      <th style={{ ...s.th, textAlign: 'right', minWidth: 110 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesVisiveis.map(cli => (
                      <tr key={cli.id}>
                        <td style={{ ...s.td, ...stickyCol, background: '#ffffff', fontWeight: 600 }}>{cli.nome}</td>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(mes => {
                          const cel = getCelula(cli.id, mes);
                          const cor = STATUS_COR[cel.status] || STATUS_COR.pendente;
                          return (
                            <td key={mes} style={tdMes(cel.status)}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={cel.valor || ''}
                                  placeholder="0,00"
                                  onChange={e => setCelula(cli.id, mes, { valor: parseFloat(e.target.value) || 0 })}
                                  style={{ background: 'transparent', border: 'none', color: '#1c1530', fontSize: 13, width: '100%', outline: 'none', textAlign: 'right', padding: '4px 2px' }}
                                />
                                <button
                                  onClick={() => setCelula(cli.id, mes, { status: STATUS_CYCLE[cel.status] || 'pago' })}
                                  title={`Status: ${cel.status} — clique para alternar`}
                                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}
                                >
                                  {cor.icon}
                                </button>
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: '#10b981' }}>
                          {fmtMoeda(totalLinha(cli.id))}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...s.td, ...stickyCol, background: '#f7f5fb', fontWeight: 700, color: '#5f5874', textTransform: 'uppercase', fontSize: 11 }}>
                        Total mês
                      </td>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(mes => (
                        <td key={mes} style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: '#10b981', background: '#f7f5fb' }}>
                          {fmtMoeda(totalColuna(mes))}
                        </td>
                      ))}
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: '#10b981', background: '#f7f5fb', fontSize: 14 }}>
                        {fmtMoeda(totalGeral)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#5f5874', justifyContent: 'center', flexWrap: 'wrap' }}>
              <span>⏳ Pendente</span>
              <span style={{ color: '#10b981' }}>✅ Pago</span>
              <span style={{ color: '#ef4444' }}>❌ Atrasado</span>
              <span style={{ marginLeft: 8 }}>· Edite o valor digitando direto na célula. Clique no ícone para alternar o status.</span>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════
          ABA: RECORRÊNCIAS
      ════════════════════════════════════════════════════════════════════ */}
      {aba === 'recorrencias' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={s.secaoCard}>
            <div style={s.secaoHeader}><Repeat size={20} color="#3b82f6" /><div><div style={s.secaoTitulo}>Lançamentos Recorrentes</div><div style={s.secaoDesc}>Configure despesas ou receitas que se repetem automaticamente.</div></div></div>
            {!formRec && <button style={s.btnNovo} onClick={() => setFormRec({ id: Date.now(), tipo: 'DESPESA', categoria: categorias[0] || '', descricao: '', valor: '', frequencia: 'MENSAL', dataInicio: hoje(), dataFim: '', ultimoLancamento: null })}><Plus size={15} /> Nova Recorrência</button>}
          </div>

          {formRec && (
            <div style={s.formCard}>
              <h3 style={s.formTitulo}>Nova Recorrência</h3>
              <div style={s.formRow}>
                <div style={s.campo}><label style={s.label}>Tipo</label><select style={s.input} value={formRec.tipo} onChange={e => setFormRec({ ...formRec, tipo: e.target.value })}><option value="DESPESA">Despesa</option><option value="RECEITA">Receita</option></select></div>
                <div style={s.campo}><label style={s.label}>Frequência</label><select style={s.input} value={formRec.frequencia} onChange={e => setFormRec({ ...formRec, frequencia: e.target.value })}><option value="SEMANAL">Semanal</option><option value="QUINZENAL">Quinzenal</option><option value="MENSAL">Mensal</option></select></div>
                <div style={s.campo}><label style={s.label}>Categoria</label><select style={s.input} value={formRec.categoria} onChange={e => setFormRec({ ...formRec, categoria: e.target.value })}>{categorias.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              </div>
              <div style={s.formRow}>
                <div style={{ ...s.campo, flex: 2 }}><label style={s.label}>Descrição *</label><input type="text" style={s.input} value={formRec.descricao} placeholder="Ex: Aluguel escritório" onChange={e => setFormRec({ ...formRec, descricao: e.target.value })} /></div>
                <div style={s.campo}><label style={s.label}>Valor *</label><input type="number" style={s.input} value={formRec.valor} step="0.01" onChange={e => setFormRec({ ...formRec, valor: e.target.value })} /></div>
              </div>
              <div style={s.formRow}>
                <div style={s.campo}><label style={s.label}>Data Início</label><input type="date" style={s.input} value={formRec.dataInicio} onChange={e => setFormRec({ ...formRec, dataInicio: e.target.value })} /></div>
                <div style={s.campo}><label style={s.label}>Data Fim (opcional)</label><input type="date" style={s.input} value={formRec.dataFim} onChange={e => setFormRec({ ...formRec, dataFim: e.target.value })} /></div>
              </div>
              <div style={s.formActions}>
                <button style={s.btnSalvar} onClick={salvarRecorrencia}>Salvar</button>
                <button style={s.btnCancelar} onClick={() => setFormRec(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {recorrencias.length === 0
            ? <div style={s.tdVazio}>Nenhuma recorrência configurada.</div>
            : recorrencias.map(rec => (
                <div key={rec.id} style={s.recCard}>
                  <div>
                    <div style={s.recTitulo}>{rec.descricao}</div>
                    <div style={s.recMeta}>{rec.tipo} · {rec.frequencia} · {rec.categoria}</div>
                    <div style={s.recMeta}>Início: {rec.dataInicio} · Fim: {rec.dataFim || '∞'} · Último: {rec.ultimoLancamento || 'nunca'}</div>
                  </div>
                  <div style={s.acoes}>
                    <span style={{ fontSize: '1.2rem', fontWeight: 700, color: rec.tipo === 'RECEITA' ? '#10b981' : '#ef4444' }}>{fmtMoeda(rec.valor)}</span>
                    <button style={{ ...s.btnDel, background: 'rgba(59,130,246,0.15)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.4)' }} title="Regenerar — adiciona lançamentos faltantes sem apagar os existentes" onClick={() => regenerarLancamentosRecorrencia(rec)}><Repeat size={15} /></button>
                    <button style={s.btnDel} onClick={() => setRecorrencias(recorrencias.filter(r => r.id !== rec.id))}><Trash2 size={15} /></button>
                  </div>
                </div>
              ))
          }
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          ABA: ORÇAMENTOS KZEROSYSTEM
          Layout hierárquico (estilo Perfis de Acesso): módulo pai + submódulos.
          Preço por submódulo (total do módulo = soma dos selecionados) OU preço fechado do
          módulo inteiro, escolhido no seletor Soma/Fechado de cada módulo do orçamento.
      ════════════════════════════════════════════════════════════════════ */}
      {aba === 'orcamentos' && (() => {
        const catalogoAtivo = (catalogoModulosKzs && catalogoModulosKzs.length > 0)
          ? catalogoModulosKzs
          : [];
        const totalModulos = formOrc ? calcularTotalOrcamento(formOrc) : 0;
        const totaisExtras = formOrc ? calcularTotaisExtras(formOrc) : { mensal: 0, avulso: 0 };
        const totalMensalPreview = totalModulos + totaisExtras.mensal;
        const totalAnualPreview = totalMensalPreview * 12 + totaisExtras.avulso;
        const numModsSel = formOrc ? contarSubmodulosSelecionados(formOrc) : 0;
        const numExtrasValidos = formOrc ? (formOrc.itensExtras || []).filter(it => (parseFloat(it.valor) || 0) > 0).length : 0;

        const salvarOrcamento = (orc) => {
          if (!orc.clienteId && !orc.cliente?.trim()) { toast.erro('Escolha um cliente.'); return; }
          const temExtrasValidos = (orc.itensExtras || []).some(it => (parseFloat(it.valor) || 0) > 0);
          if (contarSubmodulosSelecionados(orc) === 0 && !temExtrasValidos) {
            toast.erro('Selecione ao menos um submódulo ou adicione um item em "Outros".');
            return;
          }
          const jaExiste = (orcamentosKzs || []).some(o => o.id === orc.id);
          const numeroFinal = (orc.numero || '').trim() || `ORC-${new Date().getFullYear()}-${String((orcamentosKzs || []).length + 1).padStart(3, '0')}`;
          // Numero de orcamento e o que identifica a proposta na conversa com o
          // cliente — dois iguais viram confusao no e-mail e no PDF.
          const numeroRepetido = (orcamentosKzs || []).some(
            o => o.id !== orc.id && (o.numero || '').trim().toLowerCase() === numeroFinal.trim().toLowerCase()
          );
          if (numeroRepetido) {
            toast.erro(`Já existe um orçamento com o número ${numeroFinal}. Use outro.`);
            return;
          }
          const orcSalvo = { ...orc, numero: numeroFinal, atualizadoEm: new Date().toISOString() };
          if (jaExiste) {
            setOrcamentosKzs(orcamentosKzs.map(o => o.id === orc.id ? orcSalvo : o));
          } else {
            setOrcamentosKzs([...(orcamentosKzs || []), orcSalvo]);
          }
          setOrcSalvoAviso({ numero: numeroFinal, cliente: orc.cliente || '', novo: !jaExiste });
          setFormOrc(null);
        };
        // Reconcilia orçamento existente com o catálogo: traz submódulos NOVOS e
        // remove os que sumiram, mantendo seleção, desconto e — o ponto crítico —
        // **o preço com que aquele orçamento foi fechado**.
        //
        // Até 27/08/2026 esta função sobrescrevia o preço salvo pelo do catálogo
        // ("fonte da verdade"). Consequência: abrir o ORC-2026-019 para editar
        // reprecificava tudo pela tabela vigente e o total caía de R$ 6.125 para
        // R$ 4.810 — proposta já enviada e assinada mudando de valor sozinha.
        // O catálogo é a tabela de preço de HOJE; o orçamento é o que foi
        // combinado com o cliente NAQUELE dia. O segundo manda.
        const reconciliarComCatalogo = (orcamento) => {
          const modReconciliados = catalogoAtivo.map(mCat => {
            const mSalvo = (orcamento.modulos || []).find(m => m.slug === mCat.slug);
            const subsReconciliados = mCat.submodulos.map(sCat => {
              const sSalvo = mSalvo?.submodulos?.find(s => s.slug === sCat.slug);
              return {
                slug: sCat.slug,
                label: sCat.label,
                // Submódulo já presente no orçamento mantém o preço dele; só
                // item novo herda o preço do catálogo.
                valorUnitario: sSalvo?.valorUnitario ?? sCat.valorUnitario,
                selecionado: sSalvo?.selecionado ?? false,
                descontoPct: sSalvo?.descontoPct ?? 0,
              };
            });
            return {
              slug: mCat.slug,
              label: mCat.label,
              description: mCat.description,
              valorModulo: mSalvo?.valorModulo ?? mCat.valorModulo,
              modoVenda: mSalvo?.modoVenda === 'inteiro' ? 'inteiro' : 'submodulos',
              descontoModuloPct: mSalvo?.descontoModuloPct ?? 0,
              submodulos: subsReconciliados,
            };
          });
          return {
            ...orcamento,
            modulos: modReconciliados,
            itensExtras: Array.isArray(orcamento.itensExtras) ? orcamento.itensExtras : [],
          };
        };
        const abrirNovoOrcamento = () => {
          setFormOrc({
            id: Date.now(),
            numero: '',
            cliente: '',
            clienteId: '',
            data: hoje(),
            validade: '',
            status: 'RASCUNHO',
            numEmpresas: 1,
            modulos: catalogoAtivo.map(m => ({
              slug: m.slug,
              label: m.label,
              description: m.description,
              valorModulo: m.valorModulo,
              modoVenda: 'submodulos',
              descontoModuloPct: 0,
              submodulos: m.submodulos.map(s => ({
                slug: s.slug,
                label: s.label,
                valorUnitario: s.valorUnitario,
                selecionado: false,
                descontoPct: 0,
              })),
            })),
            observacoes: '',
            condicoesEspeciais: '',
            itensExtras: [],
          });
        };
        const addItemExtra = () => {
          setFormOrc({
            ...formOrc,
            itensExtras: [
              ...(formOrc.itensExtras || []),
              { id: Date.now() + Math.random(), descricao: '', valor: '', mensal: false },
            ],
          });
        };
        // Item padrão do Catálogo entra como cópia: ajustar o valor aqui não
        // muda o cadastro nem os outros orçamentos.
        const addItemPadrao = (padraoId) => {
          const p = itensPadraoOrc.find(x => String(x.id) === String(padraoId));
          if (!p) return;
          setFormOrc({
            ...formOrc,
            itensExtras: [
              ...(formOrc.itensExtras || []),
              { id: Date.now() + Math.random(), descricao: p.descricao || '', valor: p.valor ?? '', mensal: !!p.mensal },
            ],
          });
        };
        const removeItemExtra = (id) => {
          setFormOrc({
            ...formOrc,
            itensExtras: (formOrc.itensExtras || []).filter(it => it.id !== id),
          });
        };
        const updateItemExtra = (id, campo, valor) => {
          setFormOrc({
            ...formOrc,
            itensExtras: (formOrc.itensExtras || []).map(it => it.id === id ? { ...it, [campo]: valor } : it),
          });
        };
        const editarOrcamento = (orc) => setFormOrc(reconciliarComCatalogo(orc));
        const exportarOrcamentoPDF = (orc) => {
          const cliente = (clientes || []).find(c => String(c.id) === String(orc.clienteId));
          // semCabecalho: htmlOrcamento tem cabeçalho próprio com logo KzeroSystem
          // (não usa o padrão Kzero Consultoria — este PDF é venda do produto).
          exportarPDF(
            htmlOrcamento(orc, cliente),
            `Orçamento ${orc.numero || 'KzeroSystem'} - ${cliente?.nome || orc.cliente || 'Cliente'}`,
            { semCabecalho: true }
          );
        };
        const preencherCatalogoPadrao = () => {
          const temPrecos = Object.keys(precosCatalogo).length > 0;
          if (temPrecos && !window.confirm('Isso APAGA todos os preços já preenchidos. A estrutura do catálogo já vem sempre atualizada automaticamente. Zerar preços mesmo assim?')) return;
          setPrecosCatalogo({});
          // Apaga também os preços antigos guardados no navegador: com o banco
          // vazio eles seriam lidos de novo e os preços "voltariam".
          try {
            Object.keys(localStorage)
              .filter(k => k.startsWith('kzerodiary:catalogo-precos-kzs:') || k.startsWith('kzerodiary:catalogo-modulos-kzs:'))
              .forEach(k => localStorage.removeItem(k));
          } catch {}
          toast.sucesso(temPrecos ? 'Preços zerados.' : 'Catálogo já está no padrão.');
        };
        const atualizarValorModulo = (idxMod, novoValor) => {
          setCatalogoModulosKzs(catalogoAtivo.map((m, i) => i !== idxMod
            ? m
            : { ...m, valorModulo: novoValor === '' ? null : parseFloat(novoValor) }
          ));
        };
        const atualizarValorSubmodulo = (idxMod, idxSub, novoValor) => {
          setCatalogoModulosKzs(catalogoAtivo.map((m, i) => {
            if (i !== idxMod) return m;
            return {
              ...m,
              submodulos: m.submodulos.map((s, j) => j === idxSub
                ? { ...s, valorUnitario: novoValor === '' ? null : parseFloat(novoValor) }
                : s
              ),
            };
          }));
        };
        const toggleSubmoduloForm = (idxMod, idxSub) => {
          setFormOrc({
            ...formOrc,
            modulos: formOrc.modulos.map((m, i) => {
              if (i !== idxMod) return m;
              return {
                ...m,
                submodulos: m.submodulos.map((s, j) => j === idxSub ? { ...s, selecionado: !s.selecionado } : s),
              };
            }),
          });
        };
        const setDescontoSubmoduloForm = (idxMod, idxSub, valor) => {
          setFormOrc({
            ...formOrc,
            modulos: formOrc.modulos.map((m, i) => {
              if (i !== idxMod) return m;
              return {
                ...m,
                submodulos: m.submodulos.map((s, j) => j === idxSub
                  ? { ...s, descontoPct: Math.min(100, Math.max(0, parseFloat(valor) || 0)) }
                  : s
                ),
              };
            }),
          });
        };
        const setModoVendaModulo = (idxMod, modo) => {
          setFormOrc({
            ...formOrc,
            modulos: formOrc.modulos.map((m, i) => i !== idxMod ? m : { ...m, modoVenda: modo }),
          });
        };
        const setDescontoModuloForm = (idxMod, valor) => {
          setFormOrc({
            ...formOrc,
            modulos: formOrc.modulos.map((m, i) => i !== idxMod
              ? m
              : { ...m, descontoModuloPct: Math.min(100, Math.max(0, parseFloat(valor) || 0)) }
            ),
          });
        };
        const marcarTodosDoModulo = (idxMod, selecionar) => {
          setFormOrc({
            ...formOrc,
            modulos: formOrc.modulos.map((m, i) => {
              if (i !== idxMod) return m;
              return {
                ...m,
                submodulos: m.submodulos.map(s => ({ ...s, selecionado: selecionar })),
              };
            }),
          });
        };
        const toggleExpanded = (slug, setter, current) => {
          const next = new Set(current);
          if (next.has(slug)) next.delete(slug); else next.add(slug);
          setter(next);
        };
        const STATUS_COR_ORC = { RASCUNHO: '#5f5874', ENVIADO: '#3b82f6', ACEITO: '#10b981', RECUSADO: '#ef4444' };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={s.secaoCard}>
              <div style={s.secaoHeader}>
                <Package size={20} color="#8b5cf6" />
                <div>
                  <div style={s.secaoTitulo}>Orçamentos do KzeroSystem</div>
                  <div style={s.secaoDesc}>Configure o catálogo de módulos com valores, depois gere orçamentos pra prospects e clientes.</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #e5e0ef', paddingBottom: 8 }}>
                <button
                  style={{ ...s.aba, ...(subAbaOrc === 'lista' ? s.abaAtiva : {}) }}
                  onClick={() => setSubAbaOrc('lista')}
                >
                  📋 Orçamentos ({(orcamentosKzs || []).length})
                </button>
                <button
                  style={{ ...s.aba, ...(subAbaOrc === 'catalogo' ? s.abaAtiva : {}) }}
                  onClick={() => setSubAbaOrc('catalogo')}
                >
                  ⚙️ Catálogo de Módulos ({(catalogoModulosKzs || []).length})
                </button>
              </div>
            </div>

            {/* ─── Sub-aba: CATÁLOGO ─── layout hierárquico tipo Perfis de Acesso */}
            {subAbaOrc === 'catalogo' && (
              <div style={s.secaoCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1c1530' }}>Catálogo de módulos oferecidos</div>
                    <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4 }}>
                      Espelha os módulos do kzerosystem (empresa atual: <strong style={{ color: '#1c1530' }}>{empresaAtiva?.nome || 'Kzero Consultoria'}</strong>).
                      Preencha o R$/mês por empresa em cada submódulo — e, se quiser vender o módulo fechado,
                      preencha também o <strong style={{ color: '#5b21b6' }}>preço do módulo inteiro</strong> no cabeçalho.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: catalogoTemMudancaNaoSalva ? '#b45309' : '#047857' }}>
                      {catalogoTemMudancaNaoSalva ? '☁️ Salvando no banco...' : '✓ Salvo no banco'}
                    </span>
                    <button style={{ ...s.btnAcao, background: '#374151' }} onClick={preencherCatalogoPadrao} title="Zera os preços de todos os submódulos. A estrutura (módulos e submódulos) sempre vem atualizada automaticamente.">
                      <RefreshCw size={15} /> Zerar preços
                    </button>
                  </div>
                </div>

                {(
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {catalogoAtivo.map((mod, idxMod) => {
                      const expanded = catalogoExpanded.has(mod.slug);
                      const preenchidos = mod.submodulos.filter(s => s.valorUnitario != null && parseFloat(s.valorUnitario) > 0).length;
                      const totalSubs = mod.submodulos.length;
                      const somaModulo = mod.submodulos.reduce((acc, s) => acc + (parseFloat(s.valorUnitario) || 0), 0);
                      return (
                        <div key={mod.slug} style={{ backgroundColor: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
                            <button
                              onClick={() => toggleExpanded(mod.slug, setCatalogoExpanded, catalogoExpanded)}
                              style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
                            >
                              <ChevronDown size={16} color="#5f5874" style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s', flexShrink: 0 }} />
                              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#1c1530', WebkitTextFillColor: '#1c1530' }}>{mod.label || '(sem nome)'}</div>
                                {mod.description && <div style={{ fontSize: 11, color: '#5f5874', marginTop: 3, fontStyle: 'italic' }}>{mod.description}</div>}
                              </div>
                            </button>
                            <div style={{ fontSize: 11, color: preenchidos === totalSubs ? '#10b981' : '#f59e0b', fontWeight: 600, textAlign: 'right', flexShrink: 0 }}>
                              {preenchidos}/{totalSubs} com preço
                            </div>
                            <div style={{ fontSize: 11, color: '#5f5874', textAlign: 'right', flexShrink: 0 }} title="Soma dos submódulos com preço preenchido">
                              soma {somaModulo > 0 ? `${fmtMoeda(somaModulo)}/mês` : '—'}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, paddingLeft: 12, borderLeft: '1px solid #e5e0ef' }}>
                              <span style={{ fontSize: 10, color: '#5b21b6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Módulo inteiro</span>
                              <span style={{ fontSize: 11, color: '#5f5874' }}>R$</span>
                              <input
                                type="number"
                                step="0.01"
                                placeholder="0,00"
                                value={mod.valorModulo ?? ''}
                                onChange={e => atualizarValorModulo(idxMod, e.target.value)}
                                style={{ ...s.input, width: 100, textAlign: 'right', padding: '4px 8px', fontSize: 12, borderColor: mod.valorModulo > 0 ? '#7c3aed' : undefined }}
                                title="Preço fechado do módulo completo (R$/mês por empresa). Deixe vazio para vender só por submódulo."
                              />
                              <span style={{ fontSize: 10, color: '#5f5874', minWidth: 55 }}>/emp/mês</span>
                            </div>
                          </div>
                          {expanded && (
                            <div style={{ borderTop: '1px solid #eeeaf5', backgroundColor: '#ffffff' }}>
                              {mod.submodulos.map((sub, idxSub) => (
                                <div
                                  key={sub.slug}
                                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px 10px 40px', borderBottom: idxSub < mod.submodulos.length - 1 ? '1px solid #eeeaf5' : 'none' }}
                                >
                                  <div style={{ flex: '1 1 auto', minWidth: 0, fontSize: 13, color: '#1c1530', WebkitTextFillColor: '#1c1530' }}>{sub.label || '(sem nome)'}</div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                                    <span style={{ fontSize: 11, color: '#5f5874' }}>R$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="0,00"
                                      value={sub.valorUnitario ?? ''}
                                      onChange={e => atualizarValorSubmodulo(idxMod, idxSub, e.target.value)}
                                      style={{ ...s.input, width: 100, textAlign: 'right', padding: '4px 8px', fontSize: 12 }}
                                      title="Valor mensal por empresa cliente"
                                    />
                                    <span style={{ fontSize: 10, color: '#5f5874', minWidth: 55 }}>/emp/mês</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Itens padrão de "Outros": setup, treinamento presencial etc.
                    No orçamento, o seletor "+ Item padrão" copia o item. */}
                <div style={{ marginTop: 20, borderTop: '1px solid #e5e0ef', paddingTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1c1530' }}>Itens padrão de "Outros"</div>
                      <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4 }}>
                        Cadastre aqui os itens que se repetem nos orçamentos. No orçamento, escolha em "+ Item padrão";
                        o valor pode ser ajustado só naquele orçamento.
                      </div>
                    </div>
                    <button type="button" onClick={addItemPadraoCadastro} style={{ ...s.btnAcao, padding: '5px 10px', fontSize: 11, background: '#1f6feb' }}>
                      + Novo item padrão
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, border: '1px solid #e5e0ef', borderRadius: 8, padding: 8 }}>
                    {itensPadraoOrc.length === 0 && (
                      <div style={{ fontSize: 12, color: '#5f5874', textAlign: 'center', padding: '12px 8px' }}>
                        Nenhum item padrão cadastrado.
                      </div>
                    )}
                    {itensPadraoOrc.map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: 8, border: '1px solid #e5e0ef', borderRadius: 6 }}>
                        <input
                          type="text"
                          placeholder="Descrição (ex: Setup inicial, Treinamento presencial)"
                          value={p.descricao}
                          onChange={e => updateItemPadraoCadastro(p.id, 'descricao', e.target.value)}
                          style={{ ...s.input, flex: '1 1 220px', minWidth: 0, fontSize: 12, padding: '6px 8px' }}
                          aria-label="Descrição do item padrão"
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Valor"
                          value={p.valor}
                          onChange={e => updateItemPadraoCadastro(p.id, 'valor', e.target.value)}
                          style={{ ...s.input, width: 110, fontSize: 12, padding: '6px 8px', textAlign: 'right' }}
                          aria-label="Valor do item padrão"
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#2d2640', cursor: 'pointer', userSelect: 'none', flexShrink: 0 }} title="Marcado: entra no mensal (e no anual x12). Desmarcado: cobrança avulsa.">
                          <input
                            type="checkbox"
                            checked={!!p.mensal}
                            onChange={e => updateItemPadraoCadastro(p.id, 'mensal', e.target.checked)}
                            style={{ width: 13, height: 13, cursor: 'pointer' }}
                          />
                          Mensal
                        </label>
                        <button
                          type="button"
                          onClick={() => removeItemPadraoCadastro(p.id)}
                          style={{ background: 'transparent', border: '1px solid #e5e0ef', color: '#dc2626', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11, flexShrink: 0 }}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ─── Sub-aba: LISTA DE ORÇAMENTOS ─── */}
            {subAbaOrc === 'lista' && (
              <div style={s.secaoCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1c1530' }}>Orçamentos gerados</div>
                    <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4 }}>
                      Cada orçamento é um documento formal em PDF pra enviar ao prospect.
                    </div>
                  </div>
                  <button style={s.btnAcao} onClick={abrirNovoOrcamento}>
                    <Plus size={15} /> Novo Orçamento
                  </button>
                </div>

                {(orcamentosKzs || []).length === 0 ? (
                  <div style={s.tdVazio}>Nenhum orçamento gerado ainda.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[...orcamentosKzs].sort((a, b) => (b.data || '').localeCompare(a.data || '')).map(orc => {
                      const cliente = (clientes || []).find(c => String(c.id) === String(orc.clienteId));
                      const extrasOrc = calcularTotaisExtras(orc);
                      const total = calcularTotalOrcamento(orc) + extrasOrc.mensal;
                      const cor = STATUS_COR_ORC[orc.status] || '#5f5874';
                      const submodulosSel = contarSubmodulosSelecionados(orc);
                      return (
                        <div key={orc.id} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10, padding: '0.9rem 1rem', display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'center' }}>
                          <div>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 12, color: '#8b5cf6', fontWeight: 700 }}>{orc.numero || '—'}</span>
                              <span style={{ fontSize: 14, fontWeight: 600, color: '#1c1530' }}>{cliente?.nome || orc.cliente || 'Cliente sem nome'}</span>
                              <span style={{ border: `1.5px solid ${cor}`, color: cor, borderRadius: 20, padding: '2px 10px', fontSize: 10, fontWeight: 700 }}>{orc.status || 'RASCUNHO'}</span>
                            </div>
                            <div style={{ fontSize: 11, color: '#5f5874', marginTop: 4 }}>
                              {submodulosSel} submódulo(s) · {orc.numEmpresas || 1} empresa(s) · {orc.data ? new Date(orc.data + 'T00:00').toLocaleDateString('pt-BR') : '—'}
                              {orc.validade && ` · válido até ${new Date(orc.validade + 'T00:00').toLocaleDateString('pt-BR')}`}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 10, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mensal</div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>{fmtMoeda(total)}</div>
                          </div>
                          <div style={s.acoes}>
                            <button style={{ ...s.btnEdit, color: '#10b981' }} title="Exportar PDF" onClick={() => exportarOrcamentoPDF(orc)}>
                              <Download size={16} />
                            </button>
                            <button style={s.btnEdit} title="Ver detalhes" onClick={() => setPreviewOrc(orc)}>
                              <Eye size={16} />
                            </button>
                            <button style={s.btnEdit} title="Editar" onClick={() => editarOrcamento(orc)}>
                              <Pencil size={16} />
                            </button>
                            <button style={s.btnDel} title="Excluir" onClick={() => {
                              if (window.confirm(`Excluir orçamento ${orc.numero}?`)) {
                                setOrcamentosKzs(orcamentosKzs.filter(o => o.id !== orc.id));
                              }
                            }}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ─── Confirmação de orçamento salvo ───
                Fica no centro da tela, acima do cadastro, e exige um clique
                pra sair. Antes era toast no canto e passava batido. */}
            {orcSalvoAviso && (
              <div
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
                onClick={() => setOrcSalvoAviso(null)}
              >
                <div
                  style={{ background: '#ffffff', border: '1px solid #10b981', borderRadius: 14, maxWidth: 420, width: '100%', padding: '1.75rem', textAlign: 'center', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <CheckCircle size={30} color="#10b981" />
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: '#1c1530', marginBottom: 6 }}>
                    {orcSalvoAviso.novo ? 'Orçamento criado' : 'Orçamento atualizado'}
                  </div>
                  <div style={{ fontSize: 14, color: '#5f5874', marginBottom: 4 }}>
                    <strong style={{ color: '#5b21b6' }}>{orcSalvoAviso.numero}</strong>
                    {orcSalvoAviso.cliente ? ` · ${orcSalvoAviso.cliente}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 18 }}>
                    Ele já aparece na lista de orçamentos gerados.
                  </div>
                  <button
                    onClick={() => setOrcSalvoAviso(null)}
                    style={{ ...s.btnAcao, width: '100%', justifyContent: 'center' }}
                  >
                    Entendi
                  </button>
                </div>
              </div>
            )}

            {/* ─── Modal: novo/editar orçamento ─── */}
            {formOrc && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setFormOrc(null)}>
                <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 14, width: '100%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                  <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e0ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, color: '#1c1530', fontSize: 16 }}>
                      {formOrc.numero ? `Editar ${formOrc.numero}` : 'Novo Orçamento KzeroSystem'}
                    </h3>
                    <button onClick={() => setFormOrc(null)} style={{ background: 'transparent', border: 'none', color: '#5f5874', cursor: 'pointer' }}>
                      <X size={18} />
                    </button>
                  </div>

                  <div style={{ padding: '1rem 1.5rem', overflowY: 'auto', flex: 1 }}>
                    <div style={s.formRow}>
                      <div style={s.campo}>
                        <label style={s.label}>Nº do orçamento</label>
                        <input
                          style={s.input}
                          value={formOrc.numero || ''}
                          onChange={e => setFormOrc({ ...formOrc, numero: e.target.value })}
                          placeholder={`ORC-${new Date().getFullYear()}-001`}
                        />
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                          Deixe em branco para o sistema numerar sozinho.
                        </div>
                      </div>
                      <div style={s.campo}>
                        <label style={s.label}>Cliente *</label>
                        <select
                          style={s.input}
                          value={formOrc.clienteId || ''}
                          onChange={e => {
                            const cli = (clientes || []).find(c => String(c.id) === String(e.target.value));
                            setFormOrc({ ...formOrc, clienteId: e.target.value, cliente: cli?.nome || '' });
                          }}
                        >
                          <option value="">— Selecione um cliente —</option>
                          {(clientes || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                        </select>
                      </div>
                      <div style={s.campo}>
                        <label style={s.label}>Nº de empresas</label>
                        <input
                          type="number"
                          min="1"
                          style={s.input}
                          value={formOrc.numEmpresas}
                          onChange={e => setFormOrc({ ...formOrc, numEmpresas: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                        />
                      </div>
                      <div style={s.campo}>
                        <label style={s.label}>Status</label>
                        <select style={s.input} value={formOrc.status} onChange={e => setFormOrc({ ...formOrc, status: e.target.value })}>
                          <option value="RASCUNHO">Rascunho</option>
                          <option value="ENVIADO">Enviado</option>
                          <option value="ACEITO">Aceito</option>
                          <option value="RECUSADO">Recusado</option>
                        </select>
                      </div>
                    </div>
                    <div style={s.formRow}>
                      <div style={s.campo}>
                        <label style={s.label}>Data</label>
                        <input type="date" style={s.input} value={formOrc.data} onChange={e => setFormOrc({ ...formOrc, data: e.target.value })} />
                      </div>
                      <div style={s.campo}>
                        <label style={s.label}>Validade</label>
                        <input type="date" style={s.input} value={formOrc.validade} onChange={e => setFormOrc({ ...formOrc, validade: e.target.value })} />
                      </div>
                    </div>

                    <div style={{ marginTop: 16, marginBottom: 8, fontSize: 13, fontWeight: 600, color: '#1c1530' }}>Módulos e submódulos</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto', border: '1px solid #e5e0ef', borderRadius: 8, padding: 8 }}>
                      {(formOrc.modulos || []).map((mod, idxMod) => {
                        const expanded = formOrcExpanded.has(mod.slug);
                        const totalSubs = mod.submodulos.length;
                        const inteiro = moduloVendidoInteiro(mod);
                        const sel = inteiro ? totalSubs : mod.submodulos.filter(x => x.selecionado).length;
                        const subtotalModulo = calcularTotalModulo(mod, formOrc.numEmpresas);
                        const ativo = moduloEstaNoOrcamento(mod);
                        const podeInteiro = mod.valorModulo != null && parseFloat(mod.valorModulo) > 0;
                        const todosMarcados = sel === totalSubs && totalSubs > 0;
                        return (
                          <div key={mod.slug} style={{ backgroundColor: ativo ? '#ede6fb' : '#ffffff', border: `1px solid ${ativo ? (inteiro ? '#7c3aed' : '#4c1d95') : '#e5e0ef'}`, borderRadius: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                              <button
                                onClick={() => toggleExpanded(mod.slug, setFormOrcExpanded, formOrcExpanded)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#5f5874', padding: 0, display: 'flex', flexShrink: 0 }}
                                title={expanded ? 'Colapsar' : 'Expandir'}
                              >
                                <ChevronDown size={16} style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
                              </button>
                              <input
                                type="checkbox"
                                checked={inteiro || todosMarcados}
                                disabled={inteiro}
                                ref={el => { if (el) el.indeterminate = !inteiro && sel > 0 && !todosMarcados; }}
                                onChange={() => marcarTodosDoModulo(idxMod, !todosMarcados)}
                                style={{ width: 15, height: 15, cursor: inteiro ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                                title={inteiro ? 'Módulo vendido inteiro — todos os submódulos estão inclusos' : (todosMarcados ? 'Desmarcar todos deste módulo' : 'Marcar todos deste módulo')}
                              />
                              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#1c1530', WebkitTextFillColor: '#1c1530' }}>{mod.label || '(sem nome)'}</div>
                                <div style={{ fontSize: 10, color: '#5b21b6', marginTop: 2 }}>
                                  {inteiro
                                    ? `módulo inteiro — ${totalSubs} submódulo(s) inclusos`
                                    : (sel > 0 ? `${sel} de ${totalSubs} selecionado(s)` : '')}
                                </div>
                              </div>
                              {/* Escolha explícita entre os dois modos de cobrança, com os
                                  dois valores à vista pra comparar antes de decidir. */}
                              {(() => {
                                const somaSel = mod.submodulos.reduce((acc, x) => acc + (x.selecionado
                                  ? calcularSubtotalSubmodulo({ valorUnitario: x.valorUnitario, numEmpresas: formOrc.numEmpresas, descontoPct: x.descontoPct })
                                  : 0), 0);
                                const fechado = calcularSubtotalModuloInteiro({
                                  valorModulo: mod.valorModulo, numEmpresas: formOrc.numEmpresas, descontoPct: mod.descontoModuloPct,
                                });
                                const opcao = (ativo2, label, valor, onClick, desabilitado, dica) => (
                                  <button
                                    type="button"
                                    onClick={onClick}
                                    disabled={desabilitado}
                                    title={dica}
                                    style={{
                                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                                      padding: '3px 10px', border: 'none', minWidth: 78,
                                      cursor: desabilitado ? 'not-allowed' : 'pointer',
                                      backgroundColor: ativo2 ? '#7c3aed' : 'transparent',
                                      color: ativo2 ? '#ffffff' : (desabilitado ? '#4b5563' : '#5b21b6'),
                                    }}
                                  >
                                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600 }}>{fmtMoeda(valor)}</span>
                                  </button>
                                );
                                return (
                                  <div style={{ display: 'flex', flexShrink: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e0ef' }}>
                                    {opcao(!inteiro, 'Soma', somaSel,
                                      () => setModoVendaModulo(idxMod, 'submodulos'), false,
                                      'Cobrar a soma dos submódulos selecionados')}
                                    {opcao(inteiro, 'Fechado', fechado,
                                      () => podeInteiro && setModoVendaModulo(idxMod, 'inteiro'), !podeInteiro,
                                      podeInteiro
                                        ? 'Cobrar o preço fechado do módulo, com todos os submódulos inclusos'
                                        : 'Preencha o preço do módulo inteiro na aba Catálogo para habilitar')}
                                  </div>
                                );
                              })()}
                              {inteiro && (
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  placeholder="0%"
                                  value={mod.descontoModuloPct || ''}
                                  onChange={e => setDescontoModuloForm(idxMod, e.target.value)}
                                  style={{ ...s.input, padding: '3px 6px', fontSize: 11, textAlign: 'right', width: 70, flexShrink: 0 }}
                                  title="Desconto % sobre o preço fechado do módulo"
                                />
                              )}
                              <div style={{ fontSize: 10, color: '#5f5874', textAlign: 'right', flexShrink: 0 }}>subtotal</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: ativo ? '#10b981' : '#5f5874', textAlign: 'right', flexShrink: 0, minWidth: 100 }}>
                                {ativo ? fmtMoeda(subtotalModulo) : '—'}
                              </div>
                            </div>
                            {expanded && (
                              <div style={{ borderTop: '1px solid #eeeaf5', background: '#ffffff' }}>
                                {mod.submodulos.map((sub, idxSub) => {
                                  const subsubtotal = calcularSubtotalSubmodulo({ valorUnitario: sub.valorUnitario, numEmpresas: formOrc.numEmpresas, descontoPct: sub.descontoPct });
                                  const semValor = sub.valorUnitario == null || parseFloat(sub.valorUnitario) <= 0;
                                  return (
                                    <div
                                      key={sub.slug}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '8px 14px 8px 36px',
                                        borderBottom: idxSub < mod.submodulos.length - 1 ? '1px solid #eeeaf5' : 'none',
                                        backgroundColor: (inteiro || sub.selecionado) ? '#f3effc' : 'transparent',
                                        opacity: inteiro ? 0.75 : 1,
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={inteiro || sub.selecionado}
                                        disabled={inteiro}
                                        onChange={() => toggleSubmoduloForm(idxMod, idxSub)}
                                        style={{ width: 14, height: 14, cursor: inteiro ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                                        title={inteiro ? 'Incluso no preço fechado do módulo' : undefined}
                                      />
                                      <div style={{ flex: '1 1 auto', minWidth: 0, fontSize: 13, color: '#1c1530', WebkitTextFillColor: '#1c1530' }}>{sub.label || '(sem nome)'}</div>
                                      <div style={{ fontSize: 11, color: inteiro ? '#5f5874' : (semValor ? '#ef4444' : '#5f5874'), textAlign: 'right', flexShrink: 0, minWidth: 90 }}>
                                        {inteiro ? '—' : (semValor ? 'sem valor' : `R$ ${parseFloat(sub.valorUnitario).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`)}
                                      </div>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        placeholder="0%"
                                        value={inteiro ? '' : (sub.descontoPct || '')}
                                        disabled={inteiro || !sub.selecionado}
                                        onChange={e => setDescontoSubmoduloForm(idxMod, idxSub, e.target.value)}
                                        style={{ ...s.input, padding: '3px 6px', fontSize: 11, textAlign: 'right', width: 70, flexShrink: 0 }}
                                        title="Desconto % neste submódulo"
                                      />
                                      <div style={{ fontSize: 12, fontWeight: 600, color: (inteiro || sub.selecionado) ? '#10b981' : '#5f5874', textAlign: 'right', flexShrink: 0, minWidth: 90 }}>
                                        {inteiro ? 'incluso' : (sub.selecionado ? fmtMoeda(subsubtotal) : '—')}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ marginTop: 16, marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1c1530' }}>Outros</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {itensPadraoOrc.length > 0 && (
                          <select
                            value=""
                            onChange={e => { addItemPadrao(e.target.value); e.target.value = ''; }}
                            style={{ ...s.input, width: 'auto', fontSize: 11, padding: '5px 8px' }}
                            aria-label="Adicionar item padrão"
                            title="Itens cadastrados na aba Catálogo"
                          >
                            <option value="">+ Item padrão</option>
                            {[...itensPadraoOrc]
                              .sort((a, b) => (a.descricao || '').localeCompare(b.descricao || '', 'pt-BR'))
                              .map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.descricao || '(sem descrição)'} — {fmtMoeda(p.valor)}{p.mensal ? '/mês' : ' avulso'}
                                </option>
                              ))}
                          </select>
                        )}
                        <button
                          type="button"
                          onClick={addItemExtra}
                          style={{ ...s.btnAcao, padding: '5px 10px', fontSize: 11, background: '#1f6feb' }}
                          title="Adicionar item livre (setup, treinamento, etc.)"
                        >
                          + Adicionar item
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, border: '1px solid #e5e0ef', borderRadius: 8, padding: 8 }}>
                      {(formOrc.itensExtras || []).length === 0 && (
                        <div style={{ fontSize: 12, color: '#5f5874', textAlign: 'center', padding: '12px 8px' }}>
                          Nenhum item extra. Use "Adicionar item" para setup, treinamento presencial, hora extra de consultoria, etc.
                        </div>
                      )}
                      {(formOrc.itensExtras || []).map((it) => {
                        const valorNum = parseFloat(it.valor) || 0;
                        return (
                          <div
                            key={it.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: 8,
                              background: valorNum > 0 ? '#ede6fb' : '#ffffff',
                              border: `1px solid ${valorNum > 0 ? '#4c1d95' : '#e5e0ef'}`,
                              borderRadius: 6,
                            }}
                          >
                            <input
                              type="text"
                              placeholder="Descrição (ex: Setup inicial, Treinamento presencial)"
                              value={it.descricao}
                              onChange={e => updateItemExtra(it.id, 'descricao', e.target.value)}
                              style={{ ...s.input, flex: '1 1 auto', minWidth: 0, fontSize: 12, padding: '6px 8px' }}
                            />
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Valor"
                              value={it.valor}
                              onChange={e => updateItemExtra(it.id, 'valor', e.target.value)}
                              style={{ ...s.input, width: 110, fontSize: 12, padding: '6px 8px', textAlign: 'right' }}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#2d2640', cursor: 'pointer', userSelect: 'none', flexShrink: 0 }} title="Marcado: entra no mensal (e no anual x12). Desmarcado: cobrança avulsa (soma só uma vez no anual).">
                              <input
                                type="checkbox"
                                checked={!!it.mensal}
                                onChange={e => updateItemExtra(it.id, 'mensal', e.target.checked)}
                                style={{ width: 13, height: 13, cursor: 'pointer' }}
                              />
                              Mensal
                            </label>
                            <button
                              type="button"
                              onClick={() => removeItemExtra(it.id)}
                              style={{ background: 'transparent', border: '1px solid #e5e0ef', color: '#dc2626', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11, flexShrink: 0 }}
                              title="Remover item"
                            >
                              Remover
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    <div style={s.formRow}>
                      <div style={s.campo}>
                        <label style={s.label}>Condições especiais (opcional)</label>
                        <textarea rows={2} style={{ ...s.input, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }} value={formOrc.condicoesEspeciais} onChange={e => setFormOrc({ ...formOrc, condicoesEspeciais: e.target.value })} placeholder="Ex: 3 meses de desconto na implantação" />
                      </div>
                      <div style={s.campo}>
                        <label style={s.label}>Observações (opcional)</label>
                        <textarea rows={2} style={{ ...s.input, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }} value={formOrc.observacoes} onChange={e => setFormOrc({ ...formOrc, observacoes: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e0ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Total mensal · {numModsSel} submódulo(s){numExtrasValidos > 0 ? ` + ${numExtrasValidos} extra(s)` : ''}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#10b981' }}>{fmtMoeda(totalMensalPreview)}</div>
                      <div style={{ fontSize: 11, color: '#5f5874' }}>
                        anual: {fmtMoeda(totalAnualPreview)}
                        {totaisExtras.avulso > 0 ? ` (inclui ${fmtMoeda(totaisExtras.avulso)} avulso)` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={s.btnCancelar} onClick={() => setFormOrc(null)}>Cancelar</button>
                      <button style={{ ...s.btnSalvar, background: '#3b82f6' }} onClick={() => { salvarOrcamento(formOrc); exportarOrcamentoPDF(formOrc); }}>
                        <Download size={14} style={{ marginRight: 6 }} /> Salvar + Exportar PDF
                      </button>
                      <button style={s.btnSalvar} onClick={() => salvarOrcamento(formOrc)}>Salvar</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ─── Modal: preview orçamento ─── */}
            {previewOrc && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setPreviewOrc(null)}>
                <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 14, width: '100%', maxWidth: 560, padding: '1.5rem' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#8b5cf6', fontWeight: 700 }}>{previewOrc.numero}</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#1c1530', marginTop: 2 }}>{(clientes || []).find(c => String(c.id) === String(previewOrc.clienteId))?.nome || previewOrc.cliente}</div>
                      <div style={{ fontSize: 12, color: '#5f5874', marginTop: 4 }}>{previewOrc.numEmpresas} empresa(s) · {contarSubmodulosSelecionados(previewOrc)} submódulo(s)</div>
                    </div>
                    <button onClick={() => setPreviewOrc(null)} style={{ background: 'transparent', border: 'none', color: '#5f5874', cursor: 'pointer' }}>
                      <X size={18} />
                    </button>
                  </div>
                  <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '0.75rem', marginBottom: 12, maxHeight: 380, overflowY: 'auto' }}>
                    {(previewOrc.modulos || []).map(mod => {
                      const inteiroPrev = moduloVendidoInteiro(mod);
                      const subsSel = inteiroPrev ? (mod.submodulos || []) : (mod.submodulos || []).filter(x => x.selecionado);
                      if (!moduloEstaNoOrcamento(mod)) return null;
                      const subtotalMod = calcularTotalModulo(mod, previewOrc.numEmpresas);
                      return (
                        <div key={mod.slug} style={{ marginBottom: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 12, fontWeight: 700, color: '#5b21b6', borderBottom: '1px solid #e5e0ef' }}>
                            <span>{mod.label}{inteiroPrev ? ` · módulo inteiro${mod.descontoModuloPct > 0 ? ` (-${mod.descontoModuloPct}%)` : ''}` : ''}</span>
                            <span>{fmtMoeda(subtotalMod)}</span>
                          </div>
                          {inteiroPrev
                            ? subsSel.map(sub => (
                                <div key={sub.slug} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0 3px 12px', fontSize: 11 }}>
                                  <span style={{ color: '#1c1530' }}>{sub.label}</span>
                                  <span style={{ color: '#5f5874' }}>incluso</span>
                                </div>
                              ))
                            : subsSel.map(sub => {
                            const sst = calcularSubtotalSubmodulo({ valorUnitario: sub.valorUnitario, numEmpresas: previewOrc.numEmpresas, descontoPct: sub.descontoPct });
                            return (
                              <div key={sub.slug} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0 3px 12px', fontSize: 11 }}>
                                <span style={{ color: '#1c1530' }}>{sub.label}{sub.descontoPct > 0 ? ` (-${sub.descontoPct}%)` : ''}</span>
                                <span style={{ color: '#10b981' }}>{fmtMoeda(sst)}</span>
                              </div>
                            );
                            })}
                        </div>
                      );
                    })}
                    {(() => {
                      const extrasPrev = calcularTotaisExtras(previewOrc);
                      // Mesma regra do PDF: linha com descrição aparece mesmo
                      // sem valor (itens "conforme plano").
                      const itens = (previewOrc.itensExtras || []).filter(it =>
                        (parseFloat(it.valor) || 0) > 0 || String(it.descricao || '').trim() !== ''
                      );
                      return (
                        <>
                          {itens.length > 0 && (
                            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e5e0ef' }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#2d2640', marginBottom: 6 }}>Outros</div>
                              {itens.map(it => (
                                <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0 3px 12px', fontSize: 11 }}>
                                  <span style={{ color: '#1c1530' }}>{it.descricao || '(sem descrição)'}{(parseFloat(it.valor) || 0) > 0 ? (it.mensal ? ' · mensal' : ' · avulso') : ''}</span>
                                  <span style={{ color: (parseFloat(it.valor) || 0) > 0 ? '#10b981' : '#5f5874' }}>{(parseFloat(it.valor) || 0) > 0 ? fmtMoeda(parseFloat(it.valor) || 0) : 'incluso'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, marginTop: 6, borderTop: '2px solid #e5e0ef', fontSize: 14, fontWeight: 700 }}>
                            <span style={{ color: '#1c1530' }}>Total mensal</span>
                            <span style={{ color: '#10b981' }}>{fmtMoeda(calcularTotalOrcamento(previewOrc) + extrasPrev.mensal)}</span>
                          </div>
                          {extrasPrev.avulso > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4, fontSize: 11, color: '#5f5874' }}>
                              <span>+ avulsos (uma vez)</span>
                              <span>{fmtMoeda(extrasPrev.avulso)}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button style={s.btnCancelar} onClick={() => setPreviewOrc(null)}>Fechar</button>
                    <button style={s.btnSalvar} onClick={() => { exportarOrcamentoPDF(previewOrc); setPreviewOrc(null); }}>
                      <Download size={14} style={{ marginRight: 6 }} /> Exportar PDF
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Modal de anexos PDF do lançamento (válido em qualquer aba) */}
      {anexosAbertoId != null && (() => {
        const lanc = fluxoCaixa.find(f => f.id === anexosAbertoId);
        if (!lanc) return null;
        return (
          <AnexosLancamento
            lancamento={lanc}
            podeEditar={true}
            onChange={(novos) => setAnexosLancamento(anexosAbertoId, novos)}
            onFechar={() => setAnexosAbertoId(null)}
          />
        );
      })()}
    </div>
  );
};

const s = {
  container:   { display: 'flex', flexDirection: 'column', gap: '1.25rem' },
  titulo:      { fontSize: '1.8rem', fontWeight: 700, color: '#1c1530' },
  abas:        { display: 'flex', gap: '0.5rem', borderBottom: '1px solid #e5e0ef', paddingBottom: '0.75rem', flexWrap: 'wrap' },
  aba:         { background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 20, color: '#5f5874', padding: '7px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  abaAtiva:    { background: '#7c3aed', border: '1px solid #7c3aed', color: 'white' },
  resumoGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: '1rem' },
  resumoCard:  { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  resumoLabel: { fontSize: 12, color: '#5f5874', fontWeight: 600, textTransform: 'uppercase' },
  resumoValor: { fontSize: '1.8rem', fontWeight: 700 },
  filtrosCard: { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.25rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' },
  select:      { background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '8px 12px', fontSize: 13 },
  btnNovo:     { display: 'flex', alignItems: 'center', gap: 6, background: '#7c3aed', color: 'white', border: 'none', borderRadius: 20, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', marginLeft: 'auto' },
  formCard:    { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  formTitulo:  { fontSize: '1.1rem', fontWeight: 700, color: '#1c1530', margin: 0 },
  formRow:     { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  campo:       { display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 140 },
  label:       { fontSize: 12, fontWeight: 600, color: '#5f5874', textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:       { background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, color: '#1c1530', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  formActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' },
  btnSalvar:   { background: '#7c3aed', border: 'none', borderRadius: 8, color: 'white', cursor: 'pointer', padding: '9px 22px', fontSize: 13, fontWeight: 600 },
  btnCancelar: { background: 'transparent', border: '1px solid #e5e0ef', borderRadius: 8, color: '#5f5874', cursor: 'pointer', padding: '9px 16px', fontSize: 13 },
  tabelaCard:  { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, overflow: 'hidden', maxWidth: '100%', minWidth: 0, alignSelf: 'stretch' },
  tabela:      { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '0.875rem 1rem', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#5f5874', textTransform: 'uppercase', background: '#f7f5fb', borderBottom: '1px solid #e5e0ef' },
  td:          { padding: '0.875rem 1rem', fontSize: 13, color: '#1c1530', borderBottom: '1px solid #eeeaf5' },
  tdVazio:     { padding: '2rem', textAlign: 'center', color: '#5f5874', fontSize: 13 },
  tipoBadge:   { fontSize: 11, fontWeight: 700, border: '1.5px solid', borderRadius: 20, padding: '2px 10px' },
  btnDel:      { background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4, display: 'flex' },
  btnEdit:     { background: 'transparent', border: 'none', color: '#8b5cf6', cursor: 'pointer', padding: 4, display: 'flex' },
  acoes:       { display: 'flex', gap: 4, alignItems: 'center' },
  secaoCard:   { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  secaoHeader: { display: 'flex', gap: '1rem', alignItems: 'flex-start' },
  secaoTitulo: { fontSize: 16, fontWeight: 700, color: '#1c1530' },
  secaoDesc:   { fontSize: 13, color: '#5f5874', marginTop: 4, lineHeight: 1.5 },
  btnAcao:     { display: 'flex', alignItems: 'center', gap: 8, background: '#10b981', color: 'white', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  catForm:     { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  catLista:    { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  catItem:     { display: 'flex', alignItems: 'center', gap: 6, background: '#f7f5fb', border: '1px solid #e5e0ef', borderRadius: 8, padding: '6px 12px' },
  catNome:     { fontSize: 13, color: '#1c1530' },
  recCard:     { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 12, padding: '1.1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  recTitulo:   { fontSize: 14, fontWeight: 600, color: '#1c1530' },
  recMeta:     { fontSize: 12, color: '#5f5874', marginTop: 2 },
};
